
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";

import {
  Box,
  Toolbar,
  Card,
  CardContent,
  Typography,
  Stack,
  IconButton,
  Button,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  LinearProgress,
  Checkbox,
  Backdrop,
  CircularProgress,
  ListSubheader,
  Snackbar,
  Alert
} from "@mui/material";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Sidebar from "../Components/sideBar/sideBar";
import Header from "../Components/header/Header";
import { useNavigate } from "react-router";
import {
  CloudUpload,
  DeleteOutline,
  ArrowUpward,
  ArrowDownward,
  EditOutlined,
  Visibility,
  Download,
  CheckCircle,
  Close,
  History
} from "@mui/icons-material";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  "https://buzzly-dev-api.qsstechnosoft.com/api";

const API_PREFIX = (() => {
  const base = (API_BASE || "").replace(/\/+$/, "");
  return base.endsWith("/api") ? base : `${base}/api`;
})();

async function fetchUploads(status = 'final') {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found. Please login.");

  // ✅ Build URL with status filter - FIXED
  const statusParam = status === 'all' ? '?status=all' : `?status=${status}`;

  const r = await fetch(`${API_PREFIX}/uploads${statusParam}`, {
    headers: {
      "Authorization": `Bearer ${token}`
    }
  });

  if (!r.ok) {
    const errData = await r.json().catch(() => ({}));
    throw new Error(errData.error || "Failed to fetch uploads");
  }

  return r.json();
}

// ✅ Fetch PPT download history for logged-in user
async function fetchDownloadHistory(limit = 50, offset = 0) {
  const token = localStorage.getItem("token");

  if (!token) {
    throw new Error("No token found. Please login.");
  }

  const r = await fetch(
    `${API_PREFIX}/download-history?limit=${limit}&offset=${offset}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const text = await r.text();
  console.log("Status:", r.status);
  console.log("Response:", text);

  if (!r.ok) {
    let err = {};
    try {
      err = JSON.parse(text);
    } catch {}

    throw new Error(err.error || text || "Failed to fetch download history");
  }

  return JSON.parse(text);
}

async function fetchPreviewsById(id) {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found. Please login.");

  const r = await fetch(`${API_PREFIX}/uploads/${id}/previews`, {
    headers: { "Authorization": `Bearer ${token}` }
  });

  if (!r.ok) throw new Error("Failed to fetch previews");
  return r.json();
}


function DeleteConfirmDialog({ open, onClose, onConfirm, title }) {
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose() ;
      }}
    >
      <DialogTitle sx={{ textAlign: "center" }}>Confirm Delete</DialogTitle>
      <DialogContent sx={{ textAlign: "center" }}>
        <Typography>
          Are you sure you want to delete <strong>"{title}"</strong> permanently?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" onClick={onConfirm}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

async function loadPptxGen() {
  if (window.PptxGenJS) return window.PptxGenJS;
  await new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/pptxgenjs/dist/pptxgen.bundle.js";
    s.onload = () => resolve();
    s.onerror = (e) => reject(e);
    document.head.appendChild(s);
  });
  return window.PptxGenJS;
}


async function urlToDataUrl(url) {
  // const res = await fetch(url, { credentials: "include" });
  const bust = url.includes("?") ? "&t=" : "?t=";
  const res = await fetch(url + bust + Date.now(), {
   mode: "cors",
   cache: "no-store",
   credentials: "omit",   // 🔑 no cookies/credentials
 });
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
  const blob = await res.blob();
  return await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

async function buildPptxFromImages(urls, fileName) {
  const PptxGenJS = await loadPptxGen();
  const pptx = new PptxGenJS();

  // Bigger physical canvas => LibreOffice me “small” feel nahi aayega
  // LAYOUT_WIDE = ~13.33" x 7.5"
  pptx.layout = "LAYOUT_WIDE"; // safer across viewers
  const slideW = pptx.width;   // inches
  const slideH = pptx.height;  // inches

  for (const url of urls) {
    const dataUrl = await urlToDataUrl(url);
    const slide = pptx.addSlide();

    // Fill the WHOLE slide area, keep aspect-ratio, center automatically
    slide.addImage({
      data: dataUrl,
      x: 0, y: 0,               // top-left of slide
      w: "100%", h: "100%",     // cover the full slide box
      sizing: { type: "contain", w: slideW, h: slideH } // keep ratio
    });
  }

  await pptx.writeFile({ fileName });
}

// ===== TagsDisplay Component =====
function TagsDisplay({ tags, maxVisible = 7 }) {
  const tagsArray = Array.isArray(tags) ? tags : String(tags || "").split(",").map(t => t.trim()).filter(Boolean);
  
  if (tagsArray.length === 0) return <span style={{ color: '#999' }}>No tags</span>;
  
  const visibleTags = tagsArray.slice(0, maxVisible);
  const hiddenCount = tagsArray.length - maxVisible;
  
  const displayText = visibleTags.join(", ") + (hiddenCount > 0 ? `, +${hiddenCount} more...` : "");
  
  if (hiddenCount > 0) {
    return (
      <Tooltip
        title={
          <Box sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#fff' }}>
              All Tags ({tagsArray.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {tagsArray.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    '& .MuiChip-label': { fontSize: '0.75rem' }
                  }}
                />
              ))}
            </Box>
          </Box>
        }
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'rgba(0, 0, 0, 0.9)',
              maxWidth: 450,
              minWidth: 250,
              fontSize: '0.875rem',
              padding: '12px 16px',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              '& .MuiTooltip-arrow': {
                color: 'rgba(0, 0, 0, 0.9)',
              },
            },
          },
        }}
      >
        <span style={{ cursor: 'pointer', textDecoration: 'underline dotted #4D99A3' }}>
          {displayText}
        </span>
      </Tooltip>
    );
  }
  
  return <span>{displayText}</span>;
}

// ===== TitleDisplay Component =====
function TitleDisplay({ title, maxLength = 30 }) {
  if (!title || title.trim().length === 0) {
    return <span style={{ color: '#999', fontStyle: 'italic' }}>Untitled</span>;
  }

  const titleStr = String(title).trim();
  
  // If title is within limit, show as-is
  if (titleStr.length <= maxLength) {
    return <span style={{ fontWeight: 500 }}>{titleStr}</span>;
  }
  
  // Truncate and show with tooltip
  const truncatedText = titleStr.substring(0, maxLength) + '...';
  
  return (
    <Tooltip
      title={
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#fff' }}>
            Full Title
          </Typography>
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
            {titleStr}
          </Typography>
        </Box>
      }
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            maxWidth: 350,
            minWidth: 150,
            fontSize: '0.875rem',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            '& .MuiTooltip-arrow': {
              color: 'rgba(0, 0, 0, 0.9)',
            },
          },
        },
      }}
    >
      <span style={{ 
        cursor: 'pointer', 
        textDecoration: 'underline dotted #4D99A3',
        display: 'inline-block',
        fontWeight: 500
      }}>
        {truncatedText}
      </span>
    </Tooltip>
  );
}

// Sortable Slide Item Component for Drag and Drop
function SortableSlideItem({ slideIdx, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slideIdx });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

export default function PPT() {
  const navigate = useNavigate();
  const drawerWidth = 260;

  const [searchTag, setSearchTag] = useState("");
  const searchTagInputRef = useRef(null);
 
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  // ✅ Download history UI state
  const [downloadHistoryOpen, setDownloadHistoryOpen] = useState(false);
  const [downloadHistory, setDownloadHistory] = useState([]);
  const [downloadHistoryLoading, setDownloadHistoryLoading] = useState(false);
  // ✅ Edit-before-download states
  const [openEditDownload, setOpenEditDownload] = useState(false);
  const [downloadFilename, setDownloadFilename] = useState('');
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const [pendingDownloadUrl, setPendingDownloadUrl] = useState('');
  const [editingUpload, setEditingUpload] = useState(null);
  const [editMeta, setEditMeta] = useState({
    companyName: '',
    clientName: '',
    solution: '',
    logoFile: null,
  });
  const [editMetaError, setEditMetaError] = useState('');
  const [editMetaLoading, setEditMetaLoading] = useState(false);
  const [editMetaSupported, setEditMetaSupported] = useState(false); // will come from backend  
  // ✅ Custom filename dialog for downloads
  const [downloadFilenameOpen, setDownloadFilenameOpen] = useState(false);
  const [customFilename, setCustomFilename] = useState("");
  const [pendingDownload, setPendingDownload] = useState(null);
  const [type, setType] = useState("company_specific_start");
  const [privacy, setPrivacy] = useState('public');
  const [editPrivacy, setEditPrivacy] = useState('public');
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState();
  // ✅ NEW: Modal dialog states for Title and Tags
  const [openTitleModal, setOpenTitleModal] = useState(false);
  const [openTagsModal, setOpenTagsModal] = useState(false);
  const [tempTitle, setTempTitle] = useState("");
  const [tempTags, setTempTags] = useState("");

  // ✅ NEW: Edit dialog modal states
  const [openEditTitleModal, setOpenEditTitleModal] = useState(false);
  const [openEditTagsModal, setOpenEditTagsModal] = useState(false);
  const [tempEditTitle, setTempEditTitle] = useState("");
  const [tempEditTags, setTempEditTags] = useState("");

  // ✅ NEW: Notes states for Upload dialog
  const [notes, setNotes] = useState("");
  const [openNotesModal, setOpenNotesModal] = useState(false);
  const [tempNotes, setTempNotes] = useState("");

  // ✅ NEW: Notes states for Edit dialog
  const [editNotes, setEditNotes] = useState("");
  const [openEditNotesModal, setOpenEditNotesModal] = useState(false);
  const [tempEditNotes, setTempEditNotes] = useState("");

  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [uploadStatus, setUploadStatus] = useState("final");
  const [editUploadStatus, setEditUploadStatus] = useState("final");
  const [filterStatus, setFilterStatus] = useState("final");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetUpload, setTargetUpload] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewSlides, setPreviewSlides] = useState([]);
  const [previewTitle, setPreviewTitle] = useState("");
  const allIds = useMemo(() => rows.map(r => r.id), [rows]);
  const allSelected = selectedIds.length > 0 && selectedIds.length === rows.length;
  // NEW state for single-preview
  const [currentPreviewId, setCurrentPreviewId] = useState(null);
  const [selectedSlides, setSelectedSlides] = useState([]);
  const [slideOrder, setSlideOrder] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [rowOrder, setRowOrder] = useState([]);

  // ===== Edit dialog state =====
  const [openEdit, setOpenEdit] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editTags, setEditTags] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [editFile, setEditFile] = useState(null);
  const [editFileError, setEditFileError] = useState("");
  const [editType, setEditType] = useState("company_specific_start");
  const [slideMapping, setSlideMapping] = useState([]);
  const [openCollections, setOpenCollections] = useState(false);
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsError, setCollectionsError] = useState("");

  // ✅ ADD THESE NEW STATES
  const [openSaveCollection, setOpenSaveCollection] = useState(false);
  const [saveCollectionName, setSaveCollectionName] = useState("");
  const [saveCollectionLoading, setSaveCollectionLoading] = useState(false);
  const [saveCollectionError, setSaveCollectionError] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [openDeleteCollectionConfirm, setOpenDeleteCollectionConfirm] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState(null);
  // ✅ Download history delete confirmation
  const [openDeleteHistoryConfirm, setOpenDeleteHistoryConfirm] = useState(false);
  const [historyToDelete, setHistoryToDelete] = useState(null);
  const [openRfpChoice, setOpenRfpChoice] = useState(false);
  const [openRfpUpload, setOpenRfpUpload] = useState(false);
  const [rfpFile, setRfpFile] = useState(null);
  const [rfpExtracting, setRfpExtracting] = useState(false);
  const [rfpError, setRfpError] = useState("");
  const [rfpExtracted, setRfpExtracted] = useState(null); // {customerName, projectName, projectBrief}
  const [pendingDownloadAction, setPendingDownloadAction] = useState(null);
// pendingDownloadAction = { type: 'single', row } | { type: 'preview' }

  const [pendingDownloadMeta, setPendingDownloadMeta] = useState(null);

  // ✅ NEW: Helper functions for table row reordering
  const getRowTypePriority = (id) => {
    const row = rows.find(r => r.id === id);
    return getPPTTypePriority(row?.type);
  };

  const isMoveUpDisabled = (id) => {
    const currentOrder = rowOrder.length > 0 ? rowOrder : filteredRows.map(r => r.id);
    const idx = currentOrder.indexOf(id);
    if (idx <= 0) return true;
    
    // Can't move up if different type from row above
    return getRowTypePriority(currentOrder[idx]) !== getRowTypePriority(currentOrder[idx - 1]);
  };

  const isMoveDownDisabled = (id) => {
    const currentOrder = rowOrder.length > 0 ? rowOrder : filteredRows.map(r => r.id);
    const idx = currentOrder.indexOf(id);
    if (idx === -1 || idx >= currentOrder.length - 1) return true;
    
    // Can't move down if different type from row below
    return getRowTypePriority(currentOrder[idx]) !== getRowTypePriority(currentOrder[idx + 1]);
  };

  const moveRow = (id, direction) => {
    const currentOrder = rowOrder.length > 0 ? rowOrder : filteredRows.map(r => r.id);
    const idx = currentOrder.indexOf(id);
    if (idx === -1) return;
    
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= currentOrder.length) return;
    
    // Prevent moving across type boundaries
    if (getRowTypePriority(currentOrder[idx]) !== getRowTypePriority(currentOrder[targetIdx])) return;
    
    const newOrder = [...currentOrder];
    [newOrder[idx], newOrder[targetIdx]] = [newOrder[targetIdx], newOrder[idx]];
    setRowOrder(newOrder);
  };

// Temporary save handler (Step 3 will replace this)
const handleEditSave = async () => {
  console.log('🔄 Edit save started');
  
  // Validation
  if (!editTitle.trim()) {
    setEditError("Title is required");
    return;
  }

  // Only validate file if a file is selected
  if (editFile && !isEditPpt) {
    setEditFileError("Please select a valid .ppt or .pptx file");
    return;
  }

  try {
    setEditSaving(true);
    setEditError("");
    setEditFileError("");

    // ✅ FIXED: Create FormData for file upload
    const formData = new FormData();
    formData.append('title', editTitle.trim());
    formData.append("type", editType);
    formData.append('tags', editTags.trim());
    formData.append('privacy', editPrivacy);
    formData.append('status', editUploadStatus);
    formData.append('notes', editNotes);
    
    // ✅ CRITICAL: Include file if selected
    if (editFile) {
      formData.append('file', editFile);
      console.log('📁 Including new file:', editFile.name);
    }

    const response = await fetch(`${API_PREFIX}/uploads/${editRow.id}`, {
      method: 'PUT',
      headers: {
        // ✅ IMPORTANT: Remove Content-Type header to let browser set it with boundary
        'Authorization': `Bearer ${localStorage.getItem("token")}`
      },
      body: formData  // ✅ Send FormData instead of JSON
    });

    const data = await response.json();
    
    if (data.ok) {
      await loadUploads(); // Refresh data
      setOpenEdit(false);
      console.log('✅ Edit successful:', data.fileUpdated ? 'File replaced' : 'Metadata only');
    } else {
      setEditError(data.error || 'Failed to update');
    }
  } catch (error) {
    console.error('❌ Edit save error:', error);
    setEditError('Network error: ' + error.message);
  } finally {
    setEditSaving(false);
  }
};

const openEditForRow = (row) => {
  setEditRow(row);
  setEditTitle(row?.title || "");
  const tagsStr = Array.isArray(row?.tags) ? row.tags.join(", ") : String(row?.tags || "");
  setEditTags(tagsStr);
  setEditPrivacy(row?.privacy || "public");
  setEditUploadStatus(row?.status || "final");
  setEditType(row?.type || "company_specific_start");
  setEditNotes(row?.notes || "");  // ✅ NEW: Set notes
  setEditError("");
  setEditFile(null);
  setEditFileError("");
  setOpenEdit(true);
};

const getPPTTypePriority = (type) => {
  switch (type) {
    case 'company_specific_start': return 1; // Highest priority (first)
    case 'services': return 2;
    case 'solutions': return 3;              // NEW: Solutions after services
    case 'case_study': return 4; 
    case 'company_specific_end': return 5;   // Lowest priority (last)
    default: return 6; // Unknown types go to the end
  }
};
const sortPPTsByType = (pptIds, rows) => {
  return [...pptIds].sort((idA, idB) => {
    const pptA = rows.find(row => row.id === idA);
    const pptB = rows.find(row => row.id === idB);
    
    const priorityA = getPPTTypePriority(pptA?.type);
    const priorityB = getPPTTypePriority(pptB?.type);
    
    // Sort by priority (lower number = higher priority = comes first)
    return priorityA - priorityB;
  });
};

// Handle preview for multiple PPTs
async function onPreviewSelected() {
  console.log('🎯 COMBINED PPT PREVIEW STARTING');
  
  if (!selectedIds.length) return;
  try {
    setPreviewLoading(true);
    setDownloadLoading(true);
    
    // ✅ CLEAR ALL PREVIOUS STATES FIRST
    setSelectedSlides([]);
    setSlideOrder([]);
    setSlideMapping([]);
    
    // ✅ CRITICAL: Set combined preview state
    setIsCombinedPreview(true);
    setIsReadOnlyPreview(false);
    setTargetUpload(null);
    setCurrentPreviewId(null);
    setPreviewTitle(`Selected PPTs (${selectedIds.length})`);
    setOpenPreview(true);

    
    // ✅ NEW: Sort PPT IDs by type before processing
    const sortedSelectedIds = sortPPTsByType(selectedIds, rows);
    console.log('🎯 PPT Order - Original:', selectedIds);
    console.log('🎯 PPT Order - Sorted:', sortedSelectedIds);
    
    console.log('🎯 Combined preview state set:', {
      isCombinedPreview: true,
      selectedIds: sortedSelectedIds
    });
    
    const BASE = API_PREFIX.replace(/\/api$/, '');
    const allUrls = [];
    const slideMapping = [];
    
    // Build slides and mapping with sorted order
    for (const id of sortedSelectedIds) {
      const data = await fetchPreviewsById(id);
      const urls = (data?.slides || []).map(src => `${BASE}${src}`);
      const pptInfo = rows.find(row => row.id === id);
      
      urls.forEach((url, slideIndex) => {
        allUrls.push(url);
        slideMapping.push({
          pptId: id,
          pptTitle: pptInfo?.title || 'Untitled PPT',
          slideIndex: slideIndex
        });
      });
    }
    
    setPreviewSlides(allUrls);
    setSlideMapping(slideMapping);
    
    // ✅ UPDATE: Store sorted IDs for download function
    setSelectedIds(sortedSelectedIds);
    
  } catch (e) {
    console.error("Preview selected failed:", e);
    setPreviewSlides([]);
    setSlideMapping([]);
  } finally {
    setPreviewLoading(false);
    setDownloadLoading(false); // ✅ STOP LOADING
  }
}

const handleCheckEditableForPreview = async () => {
  try {
    console.log('🚀 Function called!');
    console.log('📊 State check:', {
      isCombinedPreview,
      currentPreviewId,
      targetUpload: targetUpload?.id,
      rowsCount: rows.length
    });
    
    setEditMetaLoading(true);
    setEditMetaError('');
    
    // Get current PPT ID
    let pptId = null;
    
    if (isCombinedPreview && selectedIds.length > 1) {
      console.log('❌ Multi-PPT detected - stopping');
      // Multi-PPT preview - can't edit
      setEditMetaError('Editing is not supported for multiple PPTs');
      setEditMetaSupported(false);
      setOpenEditDownload(true);
      return;
    }
    
    if (currentPreviewId) {
      pptId = currentPreviewId;
      console.log('✅ Got pptId from currentPreviewId:', pptId);
    } else if (targetUpload?.id) {
      pptId = targetUpload.id;
      console.log('✅ Got pptId from targetUpload:', pptId);
    } else if (selectedIds.length === 1) {
      pptId = selectedIds[0];
    }
    
    if (!pptId) {
      console.log('❌ No pptId found!');
      setEditMetaError('Unable to identify PPT');
      setEditMetaSupported(false);
      setOpenEditDownload(true);
      return;
    }
    
    console.log('🔍 Searching for PPT in rows with id:', pptId);
    
    // Find PPT data
    const pptData = rows.find(r => r.id === pptId);
    console.log('📦 Found pptData:', pptData ? '✅ Yes' : '❌ No');
    if (!pptData) {
      setEditMetaError('PPT data not found');
      setEditMetaSupported(false);
      setOpenEditDownload(true);
      return;
    }
    
    setEditingUpload(pptData);
    setEditMeta({
      companyName: '',
      clientName: '',
      solution: '',
      logoFile: null,
    });
    
    const token = localStorage.getItem('token');
    if (!token) {
      setEditMetaError('Please login again');
      setEditMetaSupported(false);
      setOpenEditDownload(true);
      return;
    }
    
    // Backend call to check if editing is supported
    const response = await fetch(`${API_PREFIX}/uploads/${pptId}/check-editable`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    const data = await response.json();
    
    if (data.ok && data.editable) {
      setEditMetaSupported(true);
    } else {
      setEditMetaSupported(false);
      setEditMetaError(data.reason || 'Editing not supported for this PPT');
    }
    
    setOpenEditDownload(true);
    
  } catch (e) {
    console.error('Error checking editable:', e);
    setEditMetaError('Failed to check edit support');
    setEditMetaSupported(false);
    setOpenEditDownload(true);
  } finally {
    setEditMetaLoading(false);
  }
};

// Handle preview for single PPT
const handlePreview = async (row) => {
  console.log('🎯 SINGLE PPT PREVIEW STARTING for:', row.id);
  
  try {
    setPreviewLoading(true);
    
    // ✅ CRITICAL: Clear ALL previous states first
    setIsCombinedPreview(false);
    setIsReadOnlyPreview(true);
    setCurrentPreviewId(null);
    setTargetUpload(null);
    setSelectedSlides([]);
    setSlideOrder([]);
    setSlideMapping([]);
    
    // ✅ CRITICAL: Set single preview state
    setIsCombinedPreview(false);  // Single PPT preview
    setTargetUpload(row);         // Store PPT data
    setCurrentPreviewId(row.id);  // Store PPT ID
    setPreviewTitle(row.title);
    setOpenPreview(true);
    
    console.log('🎯 Preview state set:', {
      isCombinedPreview: false,
      currentPreviewId: row.id,
      targetUpload: row
    });
    
    // Fetch previews for this single PPT
    const data = await fetchPreviewsById(row.id);
    const BASE = API_PREFIX.replace(/\/api$/, '');
    const urls = (data?.slides || []).map(src => `${BASE}${src}`);
    
    setPreviewSlides(urls);
    console.log('🎯 Preview slides loaded:', urls.length, 'slides');
    
  } catch (e) {
    console.error("Single preview failed:", e);
    setPreviewSlides([]);
  } finally {
    setPreviewLoading(false);
  }
};

// ✅ Preview from Download History - READ-ONLY preview
const handlePreviewFromHistory = async (historyRow) => {
  try {
    setLoading(true);
    
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_PREFIX}/download-history/${historyRow.id}/preview`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = await res.json();

    if (!data.ok || !data.slides) {
      alert('Failed to load preview');
      return;
    }

    // ✅ Remove .pptx extension from display name
    let displayName = data.title || historyRow.display_filename || 'Downloaded PPT';
    if (displayName.toLowerCase().endsWith('.pptx')) {
      displayName = displayName.slice(0, -5); // Remove last 5 characters (.pptx)
    }

    // Set slides data
    setPreviewSlides(data.slides);
    setPreviewTitle(displayName); // ✅ Now shows "dfg" instead of "dfg.pptx"
    
    // READ-ONLY MODE
    setSlideOrder(data.slides.map((_, i) => i));
    setSelectedSlides([]);
    setIsCombinedPreview(false);
    setCurrentPreviewId(null);
    setIsReadOnlyPreview(true);
    setOpenPreview(true);

  } catch (error) {
    console.error('Preview from history failed:', error);
    alert('Failed to load preview');
  } finally {
    setLoading(false);
  }
};

  function toggleSlideSelection(index) {
    setSelectedSlides(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  }

  // Handle drag and drop reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSlideOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // ADD PPT Feature - Handler Functions
  const handleOpenAddPPT = async () => {
    try {
      setLoadingAddPPT(true);
      setOpenAddPPT(true);
      
      // Fetch all available PPTs
      const allPPTs = await fetchUploads();
      
      // Get currently used PPT IDs
      const currentPPTIds = new Set();
      
      if (isCombinedPreview && slideMapping.length > 0) {
        // Multi-PPT preview - get IDs from slideMapping
        slideMapping.forEach(mapping => {
          if (mapping.pptId) currentPPTIds.add(mapping.pptId);
        });
      } else if (!isCombinedPreview && currentPreviewId) {
        // Single PPT preview
        currentPPTIds.add(currentPreviewId);
      }
      
      // Filter out PPTs already in preview
      const available = allPPTs.filter(ppt => !currentPPTIds.has(ppt.id));
      setAvailablePPTs(available);
      setInsertPosition('end');
      setSelectedPPTToAdd(null);
      } catch (error) {
        console.error('Error loading available PPTs:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load PPTs',
          severity: 'error'
        });
      } finally {
      setLoadingAddPPT(false);
    }
  };

const handleAddPPTToPreview = async () => {
  if (!selectedPPTToAdd) {
    setSnackbar({
      open: true,
      message: 'Please select a PPT to add',
      severity: 'warning'
    });
    return;
  }

  try {
    setLoadingAddPPT(true);
    
    // Fetch preview slides for the selected PPT
    const data = await fetchPreviewsById(selectedPPTToAdd);
    const BASE = API_PREFIX.replace('/api', '');
    const newSlideUrls = data?.slides.map(slidePath => `${BASE}${slidePath}`) || [];
    
    if (newSlideUrls.length === 0) {
      setSnackbar({
        open: true,
        message: 'No slides found in selected PPT',
        severity: 'error'
      });
      return;
    }
    
    // Get PPT info
    const pptInfo = rows.find(row => row.id === selectedPPTToAdd);
    
    // ✅ FIX: Build current mapping with fallback for missing pptTitle
    const currentSlides = slideOrder.map(idx => previewSlides[idx]);
    const currentMapping = slideOrder.map(idx => {
      const mapping = slideMapping[idx];
      // If pptTitle is missing, look it up from rows
      if (!mapping.pptTitle || mapping.pptTitle === 'Unknown PPT') {
        const ppt = rows.find(row => row.id === mapping.pptId);
        return {
          ...mapping,
          pptTitle: ppt?.title || ppt?.originalname || 'Unknown PPT'
        };
      }
      return mapping;
    });
    
    // Calculate insert position
    let insertIndex;
    if (insertPosition === 'end') {
      insertIndex = currentSlides.length;
    } else {
      insertIndex = parseInt(insertPosition) + 1;
    }
    
    // Insert new slides
    const updatedSlides = [
      ...currentSlides.slice(0, insertIndex),
      ...newSlideUrls,
      ...currentSlides.slice(insertIndex)
    ];
    
    // Update slide mapping with new PPT
    const newMapping = [
      ...currentMapping.slice(0, insertIndex),
      ...newSlideUrls.map((_, i) => ({
        pptId: selectedPPTToAdd,
        pptTitle: pptInfo?.title || pptInfo?.originalname || 'Unknown PPT',
        slideIndex: i
      })),
      ...currentMapping.slice(insertIndex)
    ];
    
    // Update states
    setPreviewSlides(updatedSlides);
    setSlideMapping(newMapping);
    setSlideOrder(updatedSlides.map((_, index) => index));
    setIsCombinedPreview(true);
    setSelectedSlides(updatedSlides.map((_, index) => index));
    
    // Close dialog
    setOpenAddPPT(false);
    setSelectedPPTToAdd(null);
    
    setSnackbar({
      open: true,
      message: `Added ${newSlideUrls.length} slides from "${pptInfo?.title || pptInfo?.originalname}"`,
      severity: 'success'
    });

  } catch (error) {
    console.error('Error adding PPT:', error);
    setSnackbar({
      open: true,
      message: 'Failed to add PPT: ' + error.message,
      severity: 'error'
    });
  } finally {
    setLoadingAddPPT(false);
  }
};


  const handleQuickPreview = async (pptId, pptTitle) => {
    try {
      const data = await fetchPreviewsById(pptId);
      const BASE = API_PREFIX.replace('/api', '');
      const slideUrls = data?.slides.map(src => `${BASE}${src}`) || [];
      
      setQuickPreview({
        open: true,
        pptId: pptId,
        pptTitle: pptTitle,
        slides: slideUrls
      });
    } catch (error) {
      console.error('Error loading preview:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load preview',
        severity: 'error'
      });
    }
  };  

  function moveSlide(slideIdx, dir) {
    setSlideOrder(prev => {
      const pos = prev.indexOf(slideIdx);
      if (pos === -1) return prev;
      const next = [...prev];
      const swapWith = dir === 'up' ? pos - 1 : pos + 1;
      if (swapWith < 0 || swapWith >= next.length) return prev;
      [next[pos], next[swapWith]] = [next[swapWith], next[pos]];
      return next;
    });
  }
  function removeSlideFromOrder(slideIdx) {
    setSlideOrder(prev => prev.filter(i => i !== slideIdx));
    setSelectedSlides(prev => prev.filter(i => i !== slideIdx));
  }

const checkContinuity = (selectedIndexes, slideOrder) => {
  if (selectedIndexes.length <= 1) return true;
  
  // Safety check: if slideOrder is undefined or empty, fall back to old logic
  if (!slideOrder || slideOrder.length === 0) {
    const sorted = [...selectedIndexes.sort((a, b) => a - b)];
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] !== sorted[i-1] + 1) return false;
    }
    return true;
  }
  
  // Get positions of selected slides in the current slideOrder
  const positions = selectedIndexes.map(idx => slideOrder.indexOf(idx)).filter(pos => pos !== -1);
  
  if (positions.length !== selectedIndexes.length) return false; // Some slides not found in order
  
  // Sort positions and check if they are consecutive  
  const sorted = [...positions.sort((a, b) => a - b)];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] !== sorted[i-1] + 1) return false; // Found a gap
  }
  return true;
}

const canMoveSelectedUp = (selectedIndexes, slideOrder) => {
  if (selectedIndexes.length === 0) return false;
  if (!checkContinuity(selectedIndexes, slideOrder)) return false;
  
  // Find the earliest position in slideOrder
  const positions = selectedIndexes.map(idx => slideOrder.indexOf(idx));
  const minPosition = Math.min(...positions);
  
  return minPosition > 0; // Can move up if not already at top
};

const canMoveSelectedDown = (selectedIndexes, slideOrder) => {
  if (selectedIndexes.length === 0) return false;
  if (!checkContinuity(selectedIndexes, slideOrder)) return false;
  
  // Find the latest position in slideOrder  
  const positions = selectedIndexes.map(idx => slideOrder.indexOf(idx));
  const maxPosition = Math.max(...positions);
  
  return maxPosition < slideOrder.length - 1; // Can move down if not already at bottom
};

// ✅ NEW FUNCTIONS - REPLACE THE OLD ONES WITH THESE:
const moveSelectedUp = () => {
  if (!canMoveSelectedUp(selectedSlides, slideOrder)) return;
  
  setSlideOrder(prevOrder => {
    const newOrder = [...prevOrder];
    
    // Find positions of selected slides
    const positions = selectedSlides.map(idx => prevOrder.indexOf(idx));
    const minPosition = Math.min(...positions);
    
    // Move the selected group up by 1 position
    const selectedSlides_sorted = selectedSlides.slice().sort((a, b) => 
      prevOrder.indexOf(a) - prevOrder.indexOf(b)
    );
    
    // Remove selected slides from current positions
    for (const slideIdx of selectedSlides_sorted) {
      const pos = newOrder.indexOf(slideIdx);
      if (pos !== -1) {
        newOrder.splice(pos, 1);
      }
    }
    
    // Insert them 1 position earlier (collectively)
    const insertPosition = minPosition - 1;
    newOrder.splice(insertPosition, 0, ...selectedSlides_sorted);
    
    return newOrder;
  });
};

const moveSelectedDown = () => {
  if (!canMoveSelectedDown(selectedSlides, slideOrder)) return;
  
  setSlideOrder(prevOrder => {
    const newOrder = [...prevOrder];
    
    // Find positions of selected slides
    const positions = selectedSlides.map(idx => prevOrder.indexOf(idx));
    const maxPosition = Math.max(...positions);
    
    // Move the selected group down by 1 position
    const selectedSlides_sorted = selectedSlides.slice().sort((a, b) => 
      prevOrder.indexOf(a) - prevOrder.indexOf(b)
    );
    
    // Remove selected slides from current positions
    for (const slideIdx of selectedSlides_sorted) {
      const pos = newOrder.indexOf(slideIdx);
      if (pos !== -1) {
        newOrder.splice(pos, 1);
      }
    }
    
    // Insert them 1 position later (collectively)  
    const insertPosition = maxPosition - selectedSlides.length + 2;
    newOrder.splice(insertPosition, 0, ...selectedSlides_sorted);
    
    return newOrder;
  });
};

  const toggleSelectAll = (checked) => {
    setSelectedIds(checked ? allIds : []);
  };
  const toggleSelectOne = (id, checked) => {
    setSelectedIds(prev => checked ? [...new Set([...prev, id])] : prev.filter(x => x !== id));
  }

  const isPpt = useMemo(() => {
    if (!file) return false;
    const name = file.name.toLowerCase();
    return name.endsWith(".ppt") || name.endsWith(".pptx");
  }, [file]);

  const isEditPpt = useMemo(() => {
    if (!editFile) return true; // If no new file selected, it's valid
    const name = editFile.name.toLowerCase();
    return name.endsWith(".ppt") || name.endsWith(".pptx");
  }, [editFile]);

const canEditSubmit = useMemo(() => {
  // Basic validation: title must be present
  const hasTitle = editTitle.trim().length > 0;
  
  // File validation: only if file is selected
  const validFile = !editFile || (isEditPpt && editFile.size <= 200 * 1024 * 1024);
  
  return hasTitle && validFile;
}, [editTitle, isEditPpt, editFile]);

const canSubmit = useMemo(() => {
  // For Draft status: Only title is required (file optional)
  // For Final status: Both title AND file are required
  const hasTitle = title.trim().length > 0;
  const validType = ['company_specific_start', 'company_specific_end', 'services', 'case_study', 'solutions'].includes(type);
  
  if (uploadStatus === 'draft') {
    return hasTitle && validType; // ✅ File NOT required for draft
  } else {
    return isPpt && hasTitle && validType; // ✅ File required for final
  }
}, [isPpt, title, type, uploadStatus]);

  async function loadUploads() {
    try {
      setLoading(true);
      setError("");
      const data = await fetchUploads(filterStatus);  // ✅ Pass filter
      setRows(data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // ✅ ADD THIS NEW FUNCTION
  async function loadCollections() {
    try {
      setCollectionsLoading(true);
      setCollectionsError("");
      
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await fetch(`${API_PREFIX}/saved-selections`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to fetch collections");
      }

      const data = await response.json();
      setCollections(data.collections || []);
    } catch (error) {
      console.error("Error loading collections:", error);
      setCollectionsError(error.message);
    } finally {
      setCollectionsLoading(false);
    }
  }

// ✅ ADD THIS SAVE COLLECTION FUNCTION
async function saveCollection() {
  try {
    setSaveCollectionLoading(true);
    setSaveCollectionError('');

    if (!saveCollectionName.trim()) {
      setSaveCollectionError('Collection name is required');
      return;
    }

    const slidesToSave = selectedSlides.length > 0 
      ? selectedSlides 
      : slideOrder;

    const allSlides = [];
    const selectedSlideIndices = slidesToSave.slice();

    console.log('SAVING COLLECTION', {
      totalSlides: slideOrder.length,
      selectedCount: selectedSlides.length,
      actualSaveCount: slidesToSave.length,
      saveAll: selectedSlides.length === 0
    });

    // Build data for ALL slides
    for (let i = 0; i < slideOrder.length; i++) {
      const slideIdx = slideOrder[i];

      if (isCombinedPreview && slideMapping.length > slideIdx) {
        // ✅ Multi-PPT: Use slideMapping
        const mappingInfo = slideMapping[slideIdx];
        
        allSlides.push({
          pptId: mappingInfo.pptId,
          slideIndex: mappingInfo.slideIndex,
          pptTitle: mappingInfo.pptTitle || 'Unknown',
          pptFilename: `ppt_${mappingInfo.pptId}.pptx`,
          isSelected: slidesToSave.includes(slideIdx)
        });

      } else {
        // ✅ Single PPT: Get pptId from rows
        const pptInfo = rows.find(row => row.id === currentPreviewId);
        
        if (!pptInfo) {
          console.error('❌ Cannot find PPT info for currentPreviewId:', currentPreviewId);
          setSaveCollectionError('Cannot save collection: PPT not found');
          return;
        }

        allSlides.push({
          pptId: pptInfo.id,
          slideIndex: slideIdx,
          pptTitle: pptInfo.title || 'Unknown',
          pptFilename: pptInfo.filename || 'unknown.pptx',
          isSelected: slidesToSave.includes(slideIdx)
        });
      }
    }

    const selectionData = {
      slides: allSlides,
      selectedSlideIndices: selectedSlideIndices,
      originalOrder: slideOrder
    };

    console.log('📤 Sending to backend:', selectionData);

    const token = localStorage.getItem('token');
    const response = await fetch(`${API_PREFIX}/saved-selections`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        name: saveCollectionName.trim(),
        selectionData: selectionData
      })
    });

    const data = await response.json();

    if (data.ok) {
      setOpenSaveCollection(false);
      setSaveCollectionName('');
      setSaveCollectionError('');
      setSnackbar({
        open: true,
        message: `Collection "${saveCollectionName}" saved successfully!`,
        severity: 'success'
      });

      if (openCollections) {
        loadCollections();
      }
    } else {
      setSaveCollectionError(data.error || 'Failed to save collection');
    }

  } catch (error) {
    console.error('Error saving collection:', error);
    setSaveCollectionError('Network error: ' + error.message);
  } finally {
    setSaveCollectionLoading(false);
  }
}

  // ✅ ADD THIS DELETE COLLECTION FUNCTION
  async function deleteCollection(collectionId, collectionName) {
    try {
      const confirmed = true;
      if (!confirmed) return;

      const token = localStorage.getItem("token");
      const response = await fetch(`${API_PREFIX}/saved-selections/${collectionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.ok) {
        // Success - reload collections
        loadCollections();
        console.log(`Collection ${collectionName} deleted successfully!`);
      } else {
        alert('Failed to delete collection: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
      alert('Network error: ' + error.message);
    }
  }

// ✅ ADD THIS PREVIEW COLLECTION FUNCTION
async function previewCollection(collection) {
  try {
    setCollectionsLoading(true);
    
    console.log('📂 RAW Collection from backend:', collection);

    // ✅ FIX: Backend uses snake_case, frontend uses camelCase
    const selectionData = collection.selectionData || collection.selection_data;
    
    console.log('📂 selectionData:', selectionData);

    if (!selectionData) {
      console.error('❌ selectionData is undefined or null');
      setSnackbar({
        open: true,
        message: 'Collection has no selectionData',
        severity: 'error'
      });
      return;
    }

    if (!selectionData.slides || selectionData.slides.length === 0) {
      console.error('❌ selectionData.slides is empty or undefined');
      setSnackbar({
        open: true,
        message: 'Collection has no slides',
        severity: 'error'
      });
      return;
    }

    // Check format
    const isNewFormat = selectionData.selectedSlideIndices !== undefined;
    
    let allSlides, selectedIndices, slideOrder;

    if (isNewFormat) {
      console.log('✅ New format collection');
      allSlides = selectionData.slides;
      slideOrder = selectionData.originalOrder || allSlides.map((_, i) => i);
      
      // ✅ FIX: Restore selected slides from isSelected flag
      selectedIndices = allSlides
        .map((slide, index) => slide.isSelected ? index : -1)
        .filter(index => index !== -1);
        
    } else {
      console.log('⚠️ Old format collection');
      allSlides = selectionData.slides;
      selectedIndices = allSlides.map((_, i) => i);
      slideOrder = allSlides.map((_, i) => i);
    }

    console.log('✅ Restored selected slides:', selectedIndices);

    // 🔍 DEBUG: Check what backend returned
    console.log('🔍 ALL SLIDES DATA:', JSON.stringify(allSlides, null, 2));
    console.log('🔍 Selected indices found:', selectedIndices);

    console.log('📊 Collection data:', {
      format: isNewFormat ? 'NEW' : 'OLD',
      totalSlides: allSlides.length,
      preSelectedCount: selectedIndices.length
    });

    // Fetch slide URLs
    const slideUrls = [];
    const newSlideMapping = [];

      for (const slideInfo of allSlides) {
        try {
          // ✅ FIX: Use full URL format for fetch
          const token = localStorage.getItem('token');
          const response = await fetch(
            `${API_PREFIX}/uploads/${slideInfo.pptId}/previews/${slideInfo.slideIndex}`,
            {
              headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (response.ok) {
            const data = await response.json();
            
            // Build full URL for slide image
            const BASE = API_PREFIX.replace('/api', '');
            const slideUrl = data.slideUrl.startsWith('http') 
              ? data.slideUrl 
              : `${BASE}${data.slideUrl}`;
            
            slideUrls.push(slideUrl);

          newSlideMapping.push({
            pptId: slideInfo.pptId,
            pptTitle: slideInfo.pptTitle,
            slideIndex: slideInfo.slideIndex
          });
        }
      } catch (err) {
        console.error('Error loading slide:', err);
      }
    }

    if (slideUrls.length === 0) {
      setSnackbar({
        open: true,
        message: 'Failed to load collection slides',
        severity: 'error'
      });
      return;
    }

  setPreviewSlides(slideUrls);
  setSlideMapping(newSlideMapping);
  setSlideOrder(slideOrder);
  setIsCombinedPreview(true);
  setPreviewTitle(collection.name || 'Collection Preview');
  setOpenPreview(true);
  setOpenCollections(false);

  // ✅ FIX: Set selected slides AFTER dialog opens
  setTimeout(() => {
    setSelectedSlides(selectedIndices);
    console.log('✅ Selected slides restored:', selectedIndices);
  }, 100);


      console.log('✅ Collection loaded successfully');

    } catch (error) {
      console.error('❌ Error loading collection:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load collection: ' + error.message,
        severity: 'error'
      });
    } finally {
      setCollectionsLoading(false);
    }
  }

  // ✅ ADD DEBUG HELPER - Add this after other functions
  const debugPreviewState = useCallback((action) => {
    console.log(`🔍 DEBUG ${action}:`, {
      previewSlides: previewSlides.length,
      slideOrder: slideOrder.length,
      selectedSlides: selectedSlides.length,
      slideMapping: slideMapping.length,
      currentOrder: slideOrder,
      currentSelection: selectedSlides
    });
  }, [previewSlides, slideOrder, selectedSlides, slideMapping]);


useEffect(() => {
  loadUploads();
}, [filterStatus]);


useEffect(() => {
  if (rows.length > 0) {
    // Only set rowOrder if it's empty (initial load) or if rows changed significantly
    const currentIds = rows.map(r => r.id);
    const hasNewRows = currentIds.some(id => !rowOrder.includes(id));
    const hasRemovedRows = rowOrder.some(id => !currentIds.includes(id));
    
    if (rowOrder.length === 0 || hasNewRows || hasRemovedRows) {
      const sortedIds = sortPPTsByType(currentIds, rows);
      setRowOrder(sortedIds);
      console.log('🔄 Row order initialized/updated:', sortedIds.length, 'items');
    }
  } else {
    setRowOrder([]);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [rows]);

useEffect(() => {
  if (previewSlides.length > 0) {
    const initialOrder = previewSlides.map((_, index) => index);
    const initialSelection = []; // Empty array - no slides selected by default
    setSlideOrder(initialOrder);
    setSelectedSlides(initialSelection);
  } else {
    setSlideOrder([]);
    setSelectedSlides([]);
  }
}, [previewSlides]);

// ✅ ADD THESE TWO NEW DEBUG useEffect HOOKS
useEffect(() => {
  debugPreviewState('SLIDE_ORDER_CHANGED');
}, [slideOrder, debugPreviewState]);

useEffect(() => {
  debugPreviewState('SELECTED_SLIDES_CHANGED');  
}, [selectedSlides, debugPreviewState]);



  const handleDelete = async (row) => {
    try {
      setDeletingId(row.id);

      const r = await fetch(`${API_PREFIX}/uploads/${row.id}`, {
        method: "DELETE",
        headers: { 
    Accept: "application/json, text/plain, */*",
    "Authorization": `Bearer ${localStorage.getItem("token")}`
  },
      });

      const ct = r.headers.get("content-type") || "";
      let data = {};
      if (r.status !== 204) {
        if (ct.includes("application/json")) {
          data = await r.json().catch(() => ({}));
        } else {
          const txt = await r.text().catch(() => "");
          console.error("Delete returned non-JSON:", txt.slice(0, 200));
          data = { error: txt || `Non-JSON response (status ${r.status})` };
        }
      }

      if (r.ok && (r.status === 204 || data?.ok || data?.success)) {
        setRows((prev) => prev.filter((x) => x.id !== row.id));
        return;
      }

      alert(
        data?.error || `❌ Delete failed (HTTP ${r.status} ${r.statusText})`
      );
    } catch (e) {
      console.error("Delete error:", e);
      alert("❌ " + (e.message || "Network error"));
    } finally {
      setDeletingId(null);
    }
  };

  // Single-row download button now calls this instead of handleDownloadIndividual directly
const startDownload = (action) => {
  setRfpFile(null);
  setRfpError("");
  setRfpExtracted(null);
  setPendingDownloadAction(action);
  setOpenRfpChoice(true);
};

// Individual PPT download — goes through download-editable when replacements exist
const handleDownloadIndividual = async (row, replacements = null) => {
  if (!row?.id) {
    alert('PPT not found');
    return;
  }

  const hasReplacements = replacements &&
    Object.values(replacements).some(Boolean);

  // No replacements → download directly from S3
  if (!hasReplacements) {
    if (!row.file_url) { alert('Download URL not available for this PPT'); return; }
    handleOpenFilenameDialog(row.file_url, row.title || 'PPT Download', {
      pptId: row.id, pptTitle: row.title, pptFilename: row.filename,
      slideCount: row.slide_count || null,
    });
    return;
  }

  // With replacements → use download-editable so backend can do the text swap
  try {
    setDownloadLoading(true);
    const res = await fetch(`${API_PREFIX}/uploads/${row.id}/download-editable`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ selectedSlides: null, replacements }),
    });

    const data = await res.json();
    if (!data.ok) {
      alert(`Download failed: ${data.error || 'Unknown error'}`);
      return;
    }

    const downloadUrl = data.file;
    handleOpenFilenameDialog(downloadUrl, row.title || 'PPT Download', {
      pptId: row.id, pptTitle: row.title, pptFilename: row.filename,
      slideCount: data.slideCount || row.slide_count || null,
    });
  } catch (err) {
    alert('Download failed: ' + err.message);
  } finally {
    setDownloadLoading(false);
  }
};

  // --- Tag helpers ---
   const toTagArray = (x) =>
  String(Array.isArray(x) ? x.join(",") : x || "")
    .split(",")
    .map(t => t.trim())
    .filter(Boolean);

const allTags = useMemo(() => {
  const seen = new Map(); // lower -> original
  rows.forEach(r => {
    toTagArray(r.tags).forEach(t => {
      const k = t.toLowerCase();
      if (!seen.has(k)) seen.set(k, t);
    });
  });
  return Array.from(seen.values()).sort((a,b) => a.localeCompare(b));
}, [rows]);

  const [tagFilter, setTagFilter] = useState([]);
  const [titleFilter, setTitleFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

const filteredRows = useMemo(() => {
  let filtered = rows;

  // Filter by tags
  if (tagFilter.length > 0) {
    const wanted = new Set(tagFilter.map((t) => t.toLowerCase()));
    filtered = filtered.filter((r) => {
      const rowTags = toTagArray(r.tags).map((t) => t.toLowerCase());
      return [...wanted].some((t) => rowTags.includes(t));
    });
  }

  // Filter by title (case-insensitive partial match)
  if (titleFilter.trim()) {
    const titleSearch = titleFilter.toLowerCase().trim();
    filtered = filtered.filter((r) => 
      r.title && r.title.toLowerCase().includes(titleSearch)
    );
  }

  // Filter by type (exact match)
  if (typeFilter.trim()) {
    filtered = filtered.filter((r) => 
      r.type && r.type === typeFilter
    );
  }

  return filtered;
}, [rows, tagFilter, titleFilter, typeFilter]);

const [isCombinedPreview, setIsCombinedPreview] = useState(false);
const [isReadOnlyPreview, setIsReadOnlyPreview] = useState(false); 
// ADD PPT Feature - New State Variables
const [openAddPPT, setOpenAddPPT] = useState(false);
const [availablePPTs, setAvailablePPTs] = useState([]);
const [selectedPPTToAdd, setSelectedPPTToAdd] = useState(null);
const [insertPosition, setInsertPosition] = useState('end');
const [loadingAddPPT, setLoadingAddPPT] = useState(false);
const [snackbar, setSnackbar] = useState({
  open: false,
  message: '',
  severity: 'success'
});
const [searchAddPPT, setSearchAddPPT] = useState("");

const [quickPreview, setQuickPreview] = useState({
  open: false,
  pptId: null,
  pptTitle: '',
  slides: []
});
const [openSingleSlideDialog, setOpenSingleSlideDialog] = useState(false);
const [singleSlideUrl, setSingleSlideUrl] = useState('');
const [singleSlideNumber, setSingleSlideNumber] = useState(0);

// Drag and Drop Sensors
const sensors = useSensors(
  useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8, // Drag starts after moving 8px (prevents accidental drags)
    },
  }),
  useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  })
);

async function saveDownloadHistory(pptId, pptTitle, pptFilename, downloadUrl, slideCount, displayFilename) {
  try {
    const token = localStorage.getItem('token');
    const pptIdString = String(pptId);
    
    // ✅ DEBUG - Check what's being sent
    const payload = {
      pptId: pptIdString,
      pptTitle: pptTitle || 'Untitled',
      pptFilename: pptFilename || `ppt_${pptIdString}.pptx`,
      originalFilename: pptFilename,
      downloadUrl: downloadUrl,
      fileSizeBytes: null,
      slideCount: slideCount || null,
      displayFilename: displayFilename || null
    };
    
    console.log('📤 Sending to /download-history:', payload);
    
    const response = await fetch(`${API_PREFIX}/download-history`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    console.log('📥 Response:', data);
    
    if (data.ok) {
      console.log('✅ Download history saved successfully');
    } else {
      console.warn('⚠️ Failed to save download history:', data.error);
    }
  } catch (error) {
    console.error('❌ Error saving download history:', error);
  }
}

async function handleDownloadFromPreview(replacements = null) {
  console.log("DOWNLOAD DEBUG START");
  console.log("isCombinedPreview:", isCombinedPreview);
  console.log("currentPreviewId:", currentPreviewId);
  console.log("targetUpload:", targetUpload);
  console.log("selectedIds:", selectedIds);
  console.log("slideOrder:", slideOrder);
  console.log("selectedSlides:", selectedSlides);
  
  setDownloadLoading(true);
  
  try {
    // ✅ Use ALL slides if nothing selected
    const orderedSelectedSlides = selectedSlides.length > 0
      ? slideOrder.filter(i => selectedSlides.includes(i))
      : slideOrder;  // Use ALL slides

    console.log("orderedSelectedSlides:", orderedSelectedSlides);

    if (!orderedSelectedSlides.length) {
      console.log("NO SLIDES SELECTED OR AVAILABLE - STOPPING");
      alert("No slides available to download.");
      return;
    }
    
    // SINGLE PPT EDITABLE DOWNLOAD
    if (!isCombinedPreview && (currentPreviewId || targetUpload?.id)) {
      console.log("SINGLE PPT EDITABLE DOWNLOAD");
      const uploadId = currentPreviewId || targetUpload?.id;

      const res = await fetch(`${API_PREFIX}/uploads/${uploadId}/download-editable`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({ selectedSlides: orderedSelectedSlides, replacements }) // 👈 added
      });

      const data = await res.json();
      if (data.ok) {
        const downloadUrl = data.file.startsWith("http")
          ? data.file
          : `${API_PREFIX}${data.file}`;

        const suggestedName = targetUpload?.title || previewTitle || "Selected PPTs";

        // 👇 Pass meta for history
        const meta = {
          pptId: uploadId,
          pptTitle: targetUpload?.title || previewTitle,
          pptFilename: targetUpload?.filename || `ppt_${uploadId}.pptx`,
          slideCount: orderedSelectedSlides.length,
        };

        handleOpenFilenameDialog(downloadUrl, suggestedName, meta);

        // History save ab handleConfirmDownload se hoga
      } else {
        alert(`Download failed: ${data.error || "Unknown error"}`);
      }
      return;
    }
        
    // MULTI-PPT EDITABLE DOWNLOAD (including collections)
    if (isCombinedPreview && slideMapping && slideMapping.length > 0) {
      console.log("MULTI-PPT/COLLECTION EDITABLE DOWNLOAD");
      
      const sources = [];
      for (let i = 0; i < slideMapping.length; i++) {
        const mapping = slideMapping[i];
        if (mapping) {
          const pptInfo = rows.find(row => row.id === mapping.pptId);
          if (pptInfo) {
            sources[i] = {
              pptId: mapping.pptId,
              filename: pptInfo.filename,
              slideIndex: mapping.slideIndex,
              title: pptInfo.title,
              type: pptInfo.type
            };
          }
        }
      }
      
      console.log("Sources for multi-PPT:", sources.length, "total slides");
      
      const pptIdsFromSources = [...new Set(sources.filter(s => s && s.pptId).map(s => s.pptId))];
      const finalPptIds = sortPPTsByType(pptIdsFromSources, rows);

      console.log("pptIds being sent:", finalPptIds);

      const res = await fetch(`${API_PREFIX}/uploads/multi-export-editable`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({
          selectedSlides: orderedSelectedSlides,
          sources: sources,
          pptIds: finalPptIds,
          replacements 
        })
      });

      const data = await res.json();
      console.log("Backend response:", data);

      let downloadUrl = null;

      if (data.ok && data.download_url) {
        downloadUrl = data.download_url.startsWith("http")
          ? data.download_url
          : `${API_PREFIX}${data.download_url}`;
      } else if (data.ok && data.downloadurl) {
        downloadUrl = data.downloadurl.startsWith("http")
          ? data.downloadurl
          : `${API_PREFIX}${data.downloadurl}`;
      } else if (data.ok && data.file) {
        downloadUrl = data.file.startsWith("http")
          ? data.file
          : `${API_PREFIX}${data.file}`;
      }

      if (data.ok && downloadUrl) {
        const suggestedName = previewTitle || 'Combined PPT';

        // 👇 Pass meta for multi-PPT history
        const historyPptId = finalPptIds && finalPptIds.length > 0 ? finalPptIds[0] : 0;

        const meta = {
          pptId: historyPptId,
          pptTitle: previewTitle || 'Combined PPT',
          pptFilename: `combined_${Date.now()}.pptx`,
          slideCount: orderedSelectedSlides.length,
        };

        handleOpenFilenameDialog(downloadUrl, suggestedName, meta);

        // History save ab handleConfirmDownload se hoga
      } else {
        alert(`Multi-PPT editable download failed: ${data.error || "Unknown error"}`);
        console.error("Backend error:", data);
      }
      return;
    }

    alert("⚠️ Unable to determine download method. Please try again.");

  } catch (error) {
    console.log("JAVASCRIPT ERROR:", error);
    console.error("Download failed:", error);
    alert(`Download failed: ${error.message}`);
  } finally {
    setDownloadLoading(false);
    console.log("DOWNLOAD DEBUG END");
  }
}

// ✅ Open Download History dialog and load data
const handleOpenDownloadHistory = async () => {
  try {
    setDownloadHistoryLoading(true);
    setDownloadHistoryOpen(true);

    const data = await fetchDownloadHistory(50, 0);
    if (data.ok) {
      setDownloadHistory(data.history || []);
    } else {
      console.error("Failed to load download history:", data.error);
    }
  } catch (err) {
    console.error("Error loading download history:", err);
  } finally {
    setDownloadHistoryLoading(false);
  }
};

// ✅ Close dialog
const handleCloseDownloadHistory = () => {
  setDownloadHistoryOpen(false);
};

const handleOpenFilenameDialog = (
  downloadUrl,
  suggestedName = "Selected PPTs",
  meta = null           // 👈 NEW
) => {
  setCustomFilename(suggestedName);
  setPendingDownload(downloadUrl);
  setPendingDownloadMeta(meta); // 👈 store meta
  setDownloadFilenameOpen(true);
};

// ✅ Close filename dialog without downloading
const handleCloseFilenameDialog = () => {
  setDownloadFilenameOpen(false);
  setCustomFilename("");
  setPendingDownload(null);
};

const handleConfirmDownload = () => {
  if (!customFilename.trim() || !pendingDownload) {
    alert('Please enter a filename');
    return;
  }

  let finalFilename = customFilename.trim();
  if (!finalFilename.toLowerCase().endsWith('.pptx')) {
    finalFilename += '.pptx';
  }

  // Backend proxy URL with original fileUrl + desired filename
  const proxyUrl = `${API_PREFIX}/download-proxy?fileUrl=${encodeURIComponent(
    pendingDownload
  )}&filename=${encodeURIComponent(finalFilename)}`;

  const link = document.createElement('a');
  link.href = proxyUrl;
  link.target = '_blank';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // 👇 NEW: Save download history with user-given filename
  if (pendingDownloadMeta?.pptId) {
    const { pptId, pptTitle, pptFilename, slideCount } = pendingDownloadMeta;

    saveDownloadHistory(
      pptId,
      pptTitle,
      pptFilename,
      pendingDownload,
      slideCount,
      finalFilename       // User ka custom naam DB me save hoga
    );
  }

  handleCloseFilenameDialog();
};

async function extractRfpData(file) {
  const token = localStorage.getItem("token");
  const formData = new FormData();
  formData.append("rfp", file);


  const res = await fetch(`${API_PREFIX}/rfp/extract`, {
    method: "POST",
    headers: { 
      'Authorization': `Bearer ${token}` },
    body: formData,
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "Failed to extract RFP data");
  return data.fields; // { projectName, customerName, companyName }
}

// Called after user decides (with or without RFP) — proceeds to the real download
const proceedToDownload = async (replacements = null) => {
  setOpenRfpChoice(false);
  setOpenRfpUpload(false);

  if (!pendingDownloadAction) return;

  if (pendingDownloadAction.type === 'single') {
    handleDownloadIndividual(pendingDownloadAction.row, replacements);
  } else {
    handleDownloadFromPreview(replacements);
  }
  setPendingDownloadAction(null);
};

const handleRfpSubmit = async () => {
  if (!rfpFile) {
    setRfpError("Please choose a file first.");
    return;
  }
  try {
    setRfpExtracting(true);
    setRfpError("");
    const fields = await extractRfpData(rfpFile);
    setRfpExtracted(fields);
    // Show the extracted fields for user to confirm before downloading
  } catch (e) {
    setRfpError(e.message || "Extraction failed");
  } finally {
    setRfpExtracting(false);
  }
};

// ✅ Step 1: Open confirmation dialog
const handleDeleteHistoryItem = (historyItem) => {
  setHistoryToDelete(historyItem);
  setOpenDeleteHistoryConfirm(true);
};

// ✅ Step 2: Actual delete after confirmation
const confirmDeleteHistory = async () => {
  if (!historyToDelete?.id) return;

  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_PREFIX}/download-history/${historyToDelete.id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await res.json();

    if (!res.ok || !data.ok) {
      console.error('Failed to delete history:', data);
      alert(data.error || 'Failed to delete history entry');
      return;
    }

    // ✅ UI se bhi entry hata do
    setDownloadHistory((prev) => prev.filter((item) => item.id !== historyToDelete.id));
    
    // Close confirmation dialog
    setOpenDeleteHistoryConfirm(false);
    setHistoryToDelete(null);
  } catch (error) {
    console.error('Error deleting history item:', error);
    alert('Failed to delete history entry');
  }
};


const resetForm = () => {
  setTitle("");
  setTags("");
  setNotes("");           // ✅ NEW: Reset notes
  setFile(null);
  setProgress(0);
  setStatus("");
  setType("company_specific_start");
  setPrivacy("public");
  setUploadStatus("final");
};

    return (
      <Box sx={{ display: "flex" }}>
    {/* ✅ Enhanced Backdrop for both preview and download loading */}
    <Backdrop
      sx={{ 
        color: "#fff", 
        zIndex: (theme) => theme.zIndex.modal + 1,
        flexDirection: "column",
        gap: 2
      }}
      open={previewLoading || downloadLoading}
    >
      <CircularProgress color="inherit" size={60} />
      <Typography variant="h6" sx={{ mt: 2 }}>
        {previewLoading ? 'Loading Preview...' : downloadLoading ? 'Preparing Download...' : ''}
      </Typography>
    </Backdrop>
    <Sidebar
      drawerWidth={drawerWidth}
      onNavigate={(path) => navigate(path)}
    />

    <Box component="main" sx={{ flexGrow: 1, bgcolor: "#f9f9f9ff", minHeight: "100vh", p: 3 }}>
      <Header />
      <Toolbar />

      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#4896a1" }}>
            Manage PPT
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => {
              setOpenCollections(true);
              loadCollections();
            }}
            sx={{ 
              borderColor: "#4D99A3", 
              color: "#4D99A3",
              "&:hover": { 
                backgroundColor: "rgba(77, 153, 163, 0.1)",
                borderColor: "#4D99A3"
              }
            }}
          >
            Slide Collections
          </Button>
          {/* 👉 NEW: Download History button outside */}
          <Button
            variant="outlined"
            onClick={handleOpenDownloadHistory}
            startIcon={<History />}
            sx={{
              borderColor: '#4D99A3',
              color: '#4D99A3',
              '&:hover': {
                backgroundColor: 'rgba(77, 153, 163, 0.1)',
                borderColor: '#4D99A3'
              }
            }}
          >
            Download History
          </Button>          
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setOpenUpload(true)}
            sx={{ backgroundColor: "#4D99A3", border: "none" }}
          >
            Add
          </Button>
          <Button
            onClick={() => {
              // ... existing download logic ...
              const currentOrder = rowOrder.length > 0 ? rowOrder : sortPPTsByType(selectedIds, rows);
              const orderedSelectedIds = selectedIds.sort((a, b) => {
                const indexA = currentOrder.indexOf(a);
                const indexB = currentOrder.indexOf(b);
                return indexA - indexB;
              });
              
              console.log('🎯 Main Download - Original selection:', selectedIds);
              console.log('🎯 Main Download - Ordered by table:', orderedSelectedIds);
              
              setSelectedIds(orderedSelectedIds);
              onPreviewSelected();
            }}
            disabled={!selectedIds.length || previewLoading || downloadLoading}
            startIcon={!downloadLoading ? <Download /> : undefined}
            sx={{
              backgroundColor: "black",
              color: "white",
              "&:hover": { backgroundColor: "black", cursor:"pointer" },
              "&.Mui-disabled": { backgroundColor: "#4D99A3", color: "#fff"},
            }}
          >
            {downloadLoading ? (
              <>
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                Preparing Download...
              </>
            ) : (
              `Download (${selectedIds.length})`
            )}
          </Button>
        </Stack>
      </Stack>

      {/* ✅ NEW: Enhanced Filter Section */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2, p: 2, bgcolor: "white", borderRadius: 2, border: "1px solid #eee" }}>
        {/* Title Filter */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight="bold" sx={{fontSize:"0.9rem"}}>
            Search Title
          </Typography>
          <TextField
            size="small"
            placeholder="Search by title..."
            value={titleFilter}
            onChange={(e) => setTitleFilter(e.target.value)}
            sx={{ minWidth: 200 }}
            inputProps={{ autoComplete: "off" }}
          />
          
        </Stack>

        {/* Type Filter */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight="bold" sx={{fontSize:"0.9rem"}}>
            Filter by Type
          </Typography>
          <Select
            size="small"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            displayEmpty
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="company_specific_start">Company Specific - Start</MenuItem>
            <MenuItem value="company_specific_end">Company Specific - End</MenuItem>
            <MenuItem value="services">Services</MenuItem>
            <MenuItem value="case_study">Case Study</MenuItem>
            <MenuItem value="solutions">Solutions</MenuItem>
          </Select>
          
        </Stack>

        {/* Tags Filter */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight="bold" sx={{fontSize:"0.9rem"}}>
            Filter by Tags
          </Typography>
          <Select
            multiple
            size="small"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            renderValue={(selected) => (
              <Box
                sx={{
                  maxHeight: 56,
                  minHeight: 40,
                  maxWidth: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  py: 0.5,
                  pr: 4,
                }}
              >
                {selected.map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    size="small"
                    onDelete={() => {
                      setTagFilter((prev) => prev.filter((tag) => tag !== t));
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                  />
                ))}
              </Box>
            )}
            sx={{ 
              minWidth: 260,
              maxWidth: 300,
              maxHeight: 56,
              overflow: 'hidden',
              '& .MuiOutlinedInput-root': {
                overflow: 'hidden',
              },
              '& .MuiSelect-select': {
                maxHeight: 56,
                overflow: 'hidden !important',
                display: 'block',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                overflow: 'hidden',
              }
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 250,
                },
              },
            }}
          >
            <ListSubheader disableSticky>
              <TextField
                size="small"
                autoFocus
                placeholder="Search tags..."
                fullWidth
                inputRef={searchTagInputRef}
                value={searchTag}
                onChange={(e) => {
                  setSearchTag(e.target.value);
                  setTimeout(() => searchTagInputRef.current?.focus(), 0);
                }}
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                inputProps={{ autoComplete: "off" }}
              />
            </ListSubheader>

            {allTags
              .filter((t) => t.toLowerCase().includes(searchTag.toLowerCase()))
              .map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
            ))}
          </Select>
         
        </Stack>

        {/* Status Filter - NEW */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
            Filter by Status
          </Typography>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            size="small"
            sx={{ 
              minWidth: 150,
              backgroundColor: '#ffffffff',
              padding: '1px 8px',
              fontSize: '1rem',
              fontWeight: 400,
              color: '#141313ff'
            }}
          >
            <MenuItem value="final">Final Only</MenuItem>
            <MenuItem value="draft">Draft Only</MenuItem>
            <MenuItem value="all">Show All</MenuItem>
          </Select>
        </Stack>

        {/* Clear All Filters Button */}
        {(titleFilter || typeFilter || tagFilter.length > 0) && (
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => {
              setTitleFilter("");
              setTypeFilter("");
              setTagFilter([]);
              setSearchTag("");
            }}
            sx={{ ml: 2 }}
          >
            Clear All Filters
          </Button>
        )}
      </Stack>

      {/* Table */}
      <Card elevation={0} sx={{ border: "1px solid #eee" }}>
        <CardContent sx={{ padding: 0 }}>
          {loading && <Typography sx={{ p: 2 }}>Loading…</Typography>}
          {error && (
            <Typography color="error" sx={{ p: 2 }}>
              {error}
            </Typography>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={selectedIds.length > 0 && !allSelected}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                      sx={{
                        color: "#4D99A3",
                        "&.Mui-checked": { color: "#4D99A3" },
                        "& .MuiSvgIcon-root": { fill: "#4D99A3" },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '50%', minWidth: 200 }}>Title</TableCell>
                  <TableCell sx={{ width: '12%', minWidth: 50 }}>Type</TableCell>
                  <TableCell sx={{ width: '50%', minWidth: 200 }}>Tags</TableCell>
                  <TableCell align="center" sx={{ width: '6%', minWidth: 60 }}>Order</TableCell>
                  <TableCell sx={{ width: '4%', minWidth: 60 }}>Slides</TableCell>
                  <TableCell align="center" sx={{ width: '4%', minWidth: 70 }}>Status</TableCell>
                  <TableCell align="center" sx={{ width: '1%', minWidth: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
<TableBody>
  {(() => {
    // Create ordered rows based on rowOrder state
    const currentOrder = rowOrder.length > 0 ? rowOrder : filteredRows.map(r => r.id);
    const orderedRows = currentOrder
      .map(id => filteredRows.find(row => row.id === id))
      .filter(Boolean);
    
    return orderedRows.map((row) => {
      const checked = selectedIds.includes(row.id);
      return (
        <>
        <TableRow key={row.id}>
          <TableCell padding="checkbox">
            <Checkbox
              size="small"
              checked={checked}
              onChange={(e) => toggleSelectOne(row.id, e.target.checked)}
              sx={{
                color: "#4D99A3",
                "&.Mui-checked": { color: "#4D99A3" },
                "& .MuiSvgIcon-root": { fill: "#4D99A3" },
              }}
            />
          </TableCell>
          
          <TableCell><TitleDisplay title={row.title} maxLength={68} /></TableCell>
          <TableCell>{row.type}</TableCell>
          <TableCell>
            <TagsDisplay tags={row.tags} maxVisible={17} />
          </TableCell>

          {/* ✅ Order Column with Up/Down Buttons */}
          <TableCell align="center">
            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
              <Tooltip title="Move up (within same type)">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => moveRow(row.id, -1)}
                    disabled={isMoveUpDisabled(row.id)}
                    sx={{ 
                      color: isMoveUpDisabled(row.id) ? '#ccc' : '#4D99A3',
                      '&:hover': { backgroundColor: 'rgba(77, 153, 163, 0.1)' }
                    }}
                  >
                    <ArrowUpward fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              
              <Tooltip title="Move down (within same type)">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => moveRow(row.id, 1)}
                    disabled={isMoveDownDisabled(row.id)}
                    sx={{ 
                      color: isMoveDownDisabled(row.id) ? '#ccc' : '#4D99A3',
                      '&:hover': { backgroundColor: 'rgba(77, 153, 163, 0.1)' }
                    }}
                  >
                    <ArrowDownward fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </TableCell>

          <TableCell>{row.slide_count}</TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: row.status === 'draft' ? '#FFA726' : '#66BB6A',
                          margin: '0 auto'
                        }}
                      />
                    </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                          <Tooltip title="Edit">
                            <IconButton
                             onClick={() => openEditForRow(row)}
                             size="small"
                             aria-label="edit"
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
            
                          <Tooltip title="Preview">
                            <IconButton
                              size="small"
                              onClick={() => handlePreview(row)}
                              disabled={previewLoading}
                              aria-label="preview"
                             >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={!row.file_url ? "No file available" : downloadLoading ? "Download in progress..." : "Download"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => startDownload({ type: 'single', row })}
                                disabled={downloadLoading || !row.file_url}
                                aria-label="download"
                              >
                                <Download fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>             
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setTargetUpload(row);
                                setConfirmOpen(true);
                              }}
                              disabled={deletingId === row.id}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      </TableRow>
                      </>
                    );
                  });
                })()}
                
                {!filteredRows.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography align="center" color="text.secondary">
                        {(titleFilter || typeFilter || tagFilter.length) 
                          ? "No uploads match the selected filters."
                          : "No uploads yet."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Keep all your existing dialogs - Upload, Preview, Edit, Delete, etc. */}
      {/* ===== Upload Dialog ===== */}
      <Dialog
        open={openUpload}
        onClose={() => setOpenUpload(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload Presentation</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} component="form" onSubmit={(e)=>e.preventDefault()}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Type
              </Typography>
              <Select
                fullWidth
                size="small"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <MenuItem value="company_specific_start">Company Specific - Start</MenuItem>
                <MenuItem value="company_specific_end">Company Specific - End</MenuItem>
                <MenuItem value="case_study">Case Study</MenuItem>
                <MenuItem value="services">Services</MenuItem>
                <MenuItem value="solutions">Solutions</MenuItem>

              </Select>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Status
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    value={uploadStatus}
                    onChange={(e) => setUploadStatus(e.target.value)}
                  >
                    <MenuItem value="final">Final (Ready to Use)</MenuItem>
                    <MenuItem value="draft">Draft (Work in Progress)</MenuItem>
                  </Select>
                </Box>
            </Box>

            <TextField
              label={<>Title <span style={{ color: "crimson" }}>*</span></>}
              placeholder="Click to enter title..."
              value={title}
              size="small"
              fullWidth
              onClick={() => {
                setTempTitle(title);
                setOpenTitleModal(true);
              }}
              InputProps={{
                readOnly: true,
                style: { cursor: 'pointer' }
              }}
            />
 
            <TextField
              label="Tags (comma-separated)"
              placeholder="Click to enter tags..."
              value={tags}
              size="small"
              fullWidth
              onClick={() => {
                setTempTags(tags);
                setOpenTagsModal(true);
              }}
              InputProps={{
                readOnly: true,
                style: { cursor: 'pointer' }
              }}
            />

            <TextField
              label="Privacy"
              select
              value={privacy || 'public'}
              onChange={(e) => setPrivacy(e.target.value)}
              size="small"
              fullWidth
              margin="dense"
            >
              <MenuItem value="public">Public (visible to all users)</MenuItem>
              <MenuItem value="private">Private (only visible to me)</MenuItem>
            </TextField>

            <TextField
              label="Internal Notes/FAQ"
              placeholder="Click to add notes..."
              value={notes}
              size="small"
              fullWidth
              onClick={() => {
                setTempNotes(notes);
                setOpenNotesModal(true);
              }}
              InputProps={{
                readOnly: true,
                style: { cursor: 'pointer' }
              }}
              helperText="Click to add internal notes or FAQs"
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Upload PPT <span style={{ color: "crimson" }}>*</span>
              </Typography>
              <input
                id="ppt-input"
                type="file"
                accept=".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                style={{ display: "none" }}
                onChange={(e) => {
                  const selectedFile = e.target.files?.[0] || null;
                  console.log("File selected:", selectedFile?.name, selectedFile?.type); // ✅ Debug
                  setFile(selectedFile);
                }}
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <label htmlFor="ppt-input">
                  <Button variant="contained" component="span">
                    Choose file
                  </Button>
                </label>
                <Typography variant="body2" color="text.secondary">
                  {file
                    ? `${file.name} • ${Math.round(file.size / 1024)} KB`
                    : "Only .ppt/.pptx up to 200MB."}
                </Typography>
              </Stack>
              <input
                id="ppt-input"
                type="file"
                accept=".ppt,.pptx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const selectedFile = e.target.files?.[0] || null;
                  console.log("✅ File selected:", selectedFile?.name, selectedFile?.type);
                  setFile(selectedFile);
                }}
              />
              {file &&
                !(
                  file.name.toLowerCase().endsWith(".ppt") ||
                  file.name.toLowerCase().endsWith(".pptx")
                ) && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Invalid file type. Please choose .ppt/.pptx.
                  </Typography>
                )}
            </Box>
 
            {progress > 0 && (
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ borderRadius: 1 }}
              />
            )}
            {status && (
              <Typography
                variant="body2"
                sx={{ fontWeight: 600 }}
                color={
                  status.startsWith("✅")
                    ? "success.main"
                    : status.startsWith("❌")
                    ? "error.main"
                    : "text.primary"
                }
              >
                {status}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() =>{resetForm()
            setOpenUpload(false)}}>Cancel</Button>
          <Button
          // NEW (Direct-to-S3)
          onClick={async (e) => {
            e.preventDefault();
            const MAX_MB = 200;
            
            // ✅ NEW: Only require title (file is optional for draft)
            if (!title.trim()) {
              setStatus("❌ Please fill title.");
              setProgress(0);
              return;
            }
            
            // ✅ NEW: Only require file for 'final' status
            if (uploadStatus === 'final' && !isPpt) {
              setStatus("❌ Please select a .ppt/.pptx file for Final status.");
              setProgress(0);
              return;
            }
            
            if (file && file.size > MAX_MB * 1024 * 1024) {
              setStatus(`❌ File exceeds ${MAX_MB}MB limit.`);
              setProgress(0);
              return;
            }

            try {
              // ✅ NEW: Handle Draft without File
              if (!file && uploadStatus === 'draft') {
                setStatus("Saving draft...");
                setProgress(50);
                const token = localStorage.getItem("token");

                const recRes = await fetch(`${API_PREFIX}/uploads/record`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ 
                    type, 
                    title, 
                    tags, 
                    key: null,        // ✅ No file
                    publicUrl: null,  // ✅ No file
                    fileName: null,   // ✅ No file
                    mimeType: null,
                    sizeBytes: null,
                    privacy, 
                    status: uploadStatus,
                    notes             // ✅ NEW: Add notes
                  }),
                });

                const rec = await recRes.json();
                if (!rec.ok) throw new Error(rec.error || 'record failed');

                setProgress(100);
                setStatus('Draft saved successfully');
                setTimeout(() => {
                  resetForm()
                  setOpenUpload(false);
                  setFile(null);
                  setStatus("");
                  setProgress(0);
                  setUploadStatus("final");
                  loadUploads();
                }, 700);
                return; // ✅ IMPORTANT: Exit here for draft without file
              }

              // ✅ Continue normal flow for files (draft with file or final with file)
              setStatus("Requesting S3 URL...");
              setProgress(5);

              // a) get presigned URL
              const signRes = await fetch(`${API_PREFIX}/uploads/sign`, {
                method: 'POST',
                headers: {'Content-Type':'application/json'},
                body: JSON.stringify({ fileName: file.name, fileType: file.type })
              });
              const { ok, url, key, publicUrl, error } = await signRes.json();
              if (!ok || !url) throw new Error(error || 'sign failed');

              // b) PUT file directly to S3 (with progress)
              setStatus("Uploading to S3...");
              await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('PUT', url);
                xhr.withCredentials = false; 
                xhr.upload.onprogress = (evt) => {
                  if (evt.lengthComputable) {
                    const pct = Math.round((evt.loaded / evt.total) * 100);
                    setProgress(Math.min(98, pct));
                  }
                };
                xhr.onload = () =>
                  (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error('S3 upload failed'));
                xhr.onerror = () => reject(new Error('Network error'));
                xhr.send(file);
              });

              // c) Save metadata in DB
              setStatus("Saving metadata...");
              const token = localStorage.getItem("token");

              const recRes = await fetch(`${API_PREFIX}/uploads/record`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ type, title, tags, key, publicUrl, fileName: file.name, mimeType: file.type, sizeBytes: file.size, privacy, status: uploadStatus, notes }),
              });

              const rec = await recRes.json();
              if (!rec.ok) throw new Error(rec.error || 'record failed');

              setProgress(100);
              setStatus('Uploaded successfully');
              setTimeout(() => {
                resetForm()
                setOpenUpload(false);
                setFile(null);
                setStatus("");
                setProgress(0);
                setUploadStatus("final");
                loadUploads();
              }, 700);
            } catch (err) {
              console.error(err);
              setStatus("❌ " + err.message);
              setProgress(0);
            }
          }}

           disabled={!canSubmit}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ NEW: Title Modal Dialog */}
      <Dialog 
        open={openTitleModal} 
        onClose={() => setOpenTitleModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Enter Presentation Title
          <IconButton
            aria-label="close"
            onClick={() => setOpenTitleModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={tempTitle}
            onChange={(e) => setTempTitle(e.target.value)}
            placeholder="Enter a descriptive title for your presentation"
            helperText={`${(tempTitle || '').length} characters`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTitleModal(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setTitle(tempTitle);
              setOpenTitleModal(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ NEW: Tags Modal Dialog */}
      <Dialog 
        open={openTagsModal} 
        onClose={() => setOpenTagsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Enter Tags
          <IconButton
            aria-label="close"
            onClick={() => setOpenTagsModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tags (comma-separated)"
            type="text"
            fullWidth
            multiline
            rows={6}
            value={tempTags}
            onChange={(e) => setTempTags(e.target.value)}
            placeholder="e.g., marketing, sales, technology, annual report"
            helperText={`${(tempTags || '').split(',').filter(t => t.trim()).length} tags • ${(tempTags || '').length} characters`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTagsModal(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setTags(tempTags);
              setOpenTagsModal(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==== SINGLE PREVIEW DIALOG (with arrows + remove) ==== */}
        <Dialog
          open={openPreview}
          onClose={() => setOpenPreview(false)}
          maxWidth={false}
          sx={{ 
            '& .MuiDialog-paper': { 
              width: '95vw',
              maxWidth: '1400px',
              height: '92vh',
              margin: '16px'
            } 
          }}
        >
          <DialogTitle>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2 
            }}>
              <Box sx={{ flex: 1, minWidth: '250px' }}>
                <TitleDisplay title={previewTitle} maxLength={35} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {slideOrder.length} slides • {selectedSlides.length} selected
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                alignItems: 'center',
                flexWrap: 'wrap' 
              }}>

              {/* Up Arrow Button */}
              <Tooltip title={
                !checkContinuity(selectedSlides) 
                  ? "Select consecutive slides to move up" 
                  : "Move selected slides up by 1 step"
              }>
                <span>
                  <IconButton
                    size="small"
                    onClick={moveSelectedUp}
                    disabled={!canMoveSelectedUp(selectedSlides, slideOrder)}  // ✅ NEW LOGIC
                    sx={{ 
                      color: canMoveSelectedUp(selectedSlides, slideOrder) ? '#4D99A3' : '#ccc',  // ✅ Visual feedback
                      border: `1px solid ${canMoveSelectedUp(selectedSlides, slideOrder) ? '#4D99A3' : '#ccc'}`,
                      borderRadius: 1,
                      '&.Mui-disabled': {
                        color: '#ccc',
                        borderColor: '#ccc'
                      }
                    }}
                  >
                    <ArrowUpward fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              {/* Down Arrow Button */}
              <Tooltip title={
                !checkContinuity(selectedSlides) 
                  ? "Select consecutive slides to move down" 
                  : "Move selected slides down by 1 step"
              }>
                <span>
                  <IconButton
                    size="small"
                    onClick={moveSelectedDown}
                    disabled={!canMoveSelectedDown(selectedSlides, slideOrder)}  // ✅ NEW LOGIC
                    sx={{ 
                      color: canMoveSelectedDown(selectedSlides, slideOrder) ? '#4D99A3' : '#ccc',  // ✅ Visual feedback
                      border: `1px solid ${canMoveSelectedDown(selectedSlides, slideOrder) ? '#4D99A3' : '#ccc'}`,
                      borderRadius: 1,
                      '&.Mui-disabled': {
                        color: '#ccc',
                        borderColor: '#ccc'
                      }
                    }}
                  >
                    <ArrowDownward fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
                 
              {!isReadOnlyPreview && (
                <>
                  {/* Select All Button */}
                  <Button 
                    size="small" 
                    onClick={() => {
                      if (selectedSlides.length === slideOrder.length) {
                        setSelectedSlides([]);
                      } else {
                        setSelectedSlides([...slideOrder]);
                      }
                    }}
                  >
                    {selectedSlides.length === slideOrder.length ? 'Deselect All' : 'Select All'}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleOpenAddPPT}
                    sx={{
                      borderColor: '#4D99A3',
                      color: '#4D99A3',
                      '&:hover': {
                        backgroundColor: 'rgba(77, 153, 163, 0.1)',
                        borderColor: '#4D99A3'
                      }
                    }}
                  >
                    Add PPT
                  </Button>

                  {/* ADD SAVE COLLECTION BUTTON HERE */}
                  <Button
                    variant="outlined"
                    onClick={() => setOpenSaveCollection(true)}
                    sx={{
                      borderColor: '#4D99A3',
                      color: '#4D99A3',
                      '&:hover': {
                        backgroundColor: 'rgba(77, 153, 163, 0.1)',
                        borderColor: '#4D99A3'
                      }
                    }}
                  >
                    Save Collection
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      console.log('BUTTON CLICKED');
                      handleCheckEditableForPreview();
                    }}
                    disabled={isReadOnlyPreview || editMetaLoading}
                    startIcon={<EditOutlined />}
                  >
                    Edit
                  </Button>

                  {/* Download Button */}
                  <Button
                    variant="contained"
                    // onClick={handleDownloadFromPreview}
                    onClick={() => startDownload({ type: 'preview' })}
                    disabled={downloadLoading}
                    sx={{
                      backgroundColor: '#4D99A3',
                      border: 'none',
                      mr: 1
                    }}
                  >
                    {downloadLoading ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
                    {downloadLoading ? 'Generating File...' : `Download (${selectedSlides.length > 0 ? selectedSlides.length : 'All'})`}
                  </Button>
                </>
              )}

              </Box>

          </Box>
        </DialogTitle>

<DialogContent sx={{ 
  p: 3, 
  overflowY: 'auto',
  overflowX: 'hidden',
  height: 'calc(92vh - 140px)'
}}>
  {slideOrder?.length ? (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={slideOrder}
        strategy={rectSortingStrategy}
      >
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',        // Small: 1 column
            sm: 'repeat(2, 1fr)',        // Small: 2 columns
            md: 'repeat(4, 1fr)',        // Medium: 4 columns
            lg: 'repeat(5, 1fr)',        // Large: 5 columns
            xl: 'repeat(6, 1fr)'         // Extra large: 6 columns
          },
          gap: 1.5,                         // ✅ Reduced gap from 3 to 2
          width: '100%',
          gridAutoRows: 'minmax(auto, auto)',  // ✅ Reduced from 200px to 150px
          '@media (min-width: 1600px)': {
            gridTemplateColumns: 'repeat(7, 1fr)'  // ✅ Changed from 4 to 7
          }
        }}>

            {slideOrder.map((slideIdx) => {
              const slide = previewSlides[slideIdx];
              const isSelected = selectedSlides.includes(slideIdx);
              const selectionOrder = isSelected ? selectedSlides.indexOf(slideIdx) + 1 : null;

              return (
                <SortableSlideItem key={slideIdx} slideIdx={slideIdx}>
                  <Box
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 2,
                      border: isSelected ? '2px solid #4D99A3' : '1px solid #e0e0e0',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      height: 'auto',  // ✅ Changed from '100%'
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(77, 153, 163, 0.3)',
                        transform: 'translateY(-2px)',
                        borderColor: '#4D99A3'
                      }
                    }}
                  >
                    {/* Number Circle */}
                    {isSelected && (
                      <Box sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: '#4D99A3',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        zIndex: 2,
                        border: '2px solid white'
                      }}>
                        {selectionOrder}
                      </Box>
                    )}

                    {/* Slide Image - Top */}
                    <Box sx={{
                      width: '100%',
                      height: 80,
                      backgroundColor: '#f8f8f8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      overflow: 'hidden'
                    }}>
                      <img
                        src={typeof slide === 'string' ? slide : slide?.image || slide?.url || slide}
                        alt={`Slide ${slideIdx + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setSingleSlideUrl(typeof slide === 'string' ? slide : slide?.image || slide?.url || slide);
                          setSingleSlideNumber(slideIdx + 1);
                          setOpenSingleSlideDialog(true);
                        }}
                        onError={(e) => {
                          e.target.src = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQwIiBoZWlnaHQ9IjE4MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIiBzdHJva2U9IiNjY2MiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNjY2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+U2xpZGUgTm90IEF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=`;
                        }}
                      />
                    </Box>

                    {/* Content Section - Middle */}
                    <Box sx={{
                      flex: 0,
                      p: 0.5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.2,
                      minHeight: 'auto'
                    }}>
                      {/* Title */}
                      <Box sx={{ 
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                      <TitleDisplay 
                        title={
                          // Single PPT preview - show the PPT title directly
                          !isCombinedPreview
                            ? (previewTitle || targetUpload?.title || 'Untitled')
                            : // Combined PPT preview - use slide mapping
                            (isCombinedPreview && slideMapping.length > 0)
                            ? (() => {
                                const mappingInfo = slideMapping[slideIdx];
                                if (mappingInfo) return mappingInfo.pptTitle;
                                return 'Unknown PPT';
                              })()
                            : 'Unknown PPT'
                        }
                        maxLength={20}
                      />

                      </Box>
                      
                      {/* Slide number info */}
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          fontSize: '0.7rem',
                          mt: 0 
                        }}
                      >
                        {/* Single PPT preview - use targetUpload which should have slidecount */}
                        {!isCombinedPreview && targetUpload?.slidecount
                          ? `Slide ${slideIdx + 1} of ${targetUpload.slidecount}`
                          : // Combined PPT preview - use slideMapping to find source PPT
                          (isCombinedPreview && slideMapping && slideMapping.length > slideIdx)
                          ? (() => {
                              const mappingInfo = slideMapping[slideIdx];
                              if (mappingInfo) {
                                const sourcePpt = rows.find(row => row.id === mappingInfo.pptId);
                                if (sourcePpt?.slidecount) {
                                  return `Slide ${mappingInfo.slideIndex + 1} of ${sourcePpt.slidecount}`;
                                }
                                // Fallback if no slidecount found
                                return `Slide ${mappingInfo.slideIndex + 1}`;
                              }
                            })()
                          : // Final fallback - just slide number
                            `Slide ${slideIdx + 1}`}
                      </Typography>
                    </Box>

                  {/* Controls Section - Bottom */}

                  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 0.5, p: 0.1, borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
                    
                    {!isReadOnlyPreview && (
                      <>
                        {/* Move Up */}
                        <Tooltip title="Move up">
                          <span>
                            <IconButton size="small" onClick={() => moveSlide(slideIdx, 'up')} disabled={slideOrder.indexOf(slideIdx) === 0} sx={{ color: '#4D99A3' , p: 0.5 }}>
                              <ArrowUpward fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* Move Down */}
                        <Tooltip title="Move down">
                          <span>
                            <IconButton size="small" onClick={() => moveSlide(slideIdx, 'down')} disabled={slideOrder.indexOf(slideIdx) === slideOrder.length - 1} sx={{ color: '#4D99A3' }}>
                              <ArrowDownward fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* Delete */}
                        <Tooltip title="Remove from export">
                          <IconButton size="small" color="error" onClick={() => removeSlideFromOrder(slideIdx)}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* Selection Checkbox - Keep this outside, always visible */}
                          <Tooltip title={isSelected ? "Deselect slide" : "Select slide"}>
                            <Checkbox checked={isSelected} onChange={() => toggleSlideSelection(slideIdx)} size="small" sx={{ color: '#4D99A3', padding: '1px', '&.Mui-checked': { color: '#4D99A3' } }} />
                          </Tooltip>
                      </>
                    )}
                  </Box>

                  </Box>
                </SortableSlideItem>
              )
            })}

                </Box>
              </SortableContext>
            </DndContext>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <Typography variant="h6" color="text.secondary">
                All slides removed from export. Use "Close" to exit.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: 2, 
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Typography variant="body2" color="text.secondary">
            {selectedSlides.length} of {slideOrder.length} slides selected
          </Typography>
          
          <Button 
            onClick={() => setOpenPreview(false)}
            variant="outlined"
            sx={{ 
              borderColor: '#ccc',
              color: '#666'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download History Dialog */}
      <Dialog
        open={downloadHistoryOpen}
        onClose={handleCloseDownloadHistory}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Download History</Typography>
          <Button onClick={handleCloseDownloadHistory} startIcon={<Close />}>
            Close
          </Button>
        </DialogTitle>

        <DialogContent dividers>
          {downloadHistoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : downloadHistory.length === 0 ? (
            <Typography sx={{ py: 2, textAlign: 'center', color: '#666' }}>
              No download history found.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>Slides</TableCell>
                    <TableCell>Downloaded At</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {downloadHistory.map((row) => (
                    <TableRow key={row.id}>
                    <TableCell>
                      {(() => {
                        let name = row.display_filename || row.original_filename || row.ppt_filename || '-';
                        // Remove .pptx extension for display
                        if (name.toLowerCase().endsWith('.pptx')) {
                          name = name.slice(0, -5);
                        }
                        return name;
                      })()}
                    </TableCell>
                      <TableCell>{row.slide_count || '-'}</TableCell>

                      <TableCell>
                        {row.downloaded_at
                          ? new Date(row.downloaded_at).toLocaleString()
                          : '-'}
                      </TableCell>

                      <TableCell align="center">
                        {row.download_url ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                          >
                            {/* Preview icon */}
                            <IconButton
                              size="small"
                              onClick={() => handlePreviewFromHistory(row)}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>

                            {/* Download icon */}
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenFilenameDialog(
                                  row.download_url,
                                  row.display_filename ||
                                    row.original_filename ||
                                    row.ppt_filename ||
                                    'Downloaded PPT'
                                )
                              }
                            >
                              <Download fontSize="small" />
                            </IconButton>

                            {/* Delete history icon */}
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteHistoryItem(row)}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Stack>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {/* ✨ NEW: Edit & Download Dialog */}
      <Dialog
        open={openEditDownload}
        onClose={() => setOpenEditDownload(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Front Slide Before Download</DialogTitle>
        <DialogContent dividers>
          {editMetaLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : !editMetaSupported ? (
            <Typography color="text.secondary">
              Editing is not available for this PPT. You can download it as-is.
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Company Name"
                value={editMeta.companyName}
                onChange={(e) =>
                  setEditMeta((prev) => ({ ...prev, companyName: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Client Name"
                value={editMeta.clientName}
                onChange={(e) =>
                  setEditMeta((prev) => ({ ...prev, clientName: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Solution"
                value={editMeta.solution}
                onChange={(e) =>
                  setEditMeta((prev) => ({ ...prev, solution: e.target.value }))
                }
                fullWidth
              />

              {editMetaError && (
                <Alert severity="error">{editMetaError}</Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDownload(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!editMetaSupported) {
                // Direct download without editing
                if (editingUpload?.file_url) {
                  window.open(editingUpload.file_url, '_blank');
                }
                setOpenEditDownload(false);
                return;
              }
              
              // Apply customization
              try {
                setEditMetaLoading(true);
                setEditMetaError('');
                
                const token = localStorage.getItem('token');
                if (!token) {
                  setEditMetaError('Please login again');
                  return;
                }
                
                const formData = new FormData();
                formData.append('companyName', editMeta.companyName);
                formData.append('clientName', editMeta.clientName);
                formData.append('solution', editMeta.solution);
                if (editMeta.logoFile) {
                  formData.append('logo', editMeta.logoFile);
                }
                
                const response = await fetch(
                  `${API_PREFIX}/uploads/${editingUpload.id}/customize-download`,
                  {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`
                    },
                    body: formData
                  }
                );
                
                const data = await response.json();
                
                if (data.ok && data.downloadUrl) {
                  // Set default filename and open dialog
                  const defaultName = `${editMeta.companyName || 'Selected'} PPTs (1)`;
                  setDownloadFilename(defaultName);
                  setPendingDownloadUrl(data.downloadUrl);
                  setOpenDownloadDialog(true);
                  setOpenEditDownload(false);
                } else {
                  setEditMetaError(data.error || 'Customization failed');
                }
                
              } catch (error) {
                console.error('Customize error:', error);
                setEditMetaError('Network error: ' + error.message);
              } finally {
                setEditMetaLoading(false);
              }
            }}
            variant="contained"
            disabled={editMetaLoading}
          >
            {editMetaSupported ? 'Apply & Download' : 'Download as is'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 📥 Download Filename Dialog */}
      <Dialog
        open={openDownloadDialog}
        onClose={() => setOpenDownloadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save PPT As...</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a name for your downloaded PPT file:
          </Typography>
          <TextField
            label="Filename"
            value={downloadFilename}
            onChange={(e) => setDownloadFilename(e.target.value)}
            fullWidth
            autoFocus
            helperText=".pptx will be added automatically"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDownloadDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (downloadFilename.trim()) {
                const finalName = downloadFilename.endsWith('.pptx') 
                  ? downloadFilename 
                  : `${downloadFilename}.pptx`;
                
                try {
                  const response = await fetch(pendingDownloadUrl);
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = finalName;
                  a.click();
                  
                  window.URL.revokeObjectURL(url);
                  setOpenDownloadDialog(false);
                } catch (error) {
                  console.error('Download failed:', error);
                  alert('Download failed. Please try again.');
                }
              }
            }}
            variant="contained"
            startIcon={<span>⬇</span>}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Delete History Confirmation Dialog */}
      <Dialog
        open={openDeleteHistoryConfirm}
        onClose={() => setOpenDeleteHistoryConfirm(false)}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography>
            Are you sure you want to delete <strong>{historyToDelete?.display_filename || historyToDelete?.ppt_title || 'this PPT'}</strong> from download history?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button onClick={() => setOpenDeleteHistoryConfirm(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={confirmDeleteHistory}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>


      {/* ✅ Custom Filename Dialog */}
      <Dialog
        open={downloadFilenameOpen}
        onClose={handleCloseFilenameDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Save PPT As...
        </DialogTitle>

        <DialogContent sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Enter a name for your downloaded PPT file:
          </Typography>
          <TextField
            fullWidth
            autoFocus
            label="Filename"
            placeholder="e.g., My Presentation"
            value={customFilename}
            onChange={(e) => setCustomFilename(e.target.value)}
            helperText=".pptx will be added automatically"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleConfirmDownload();
              }
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseFilenameDialog}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDownload}
            variant="contained"
            startIcon={<Download fontSize="small" />}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          setEditSaving(false);  // ✅ Reset saving state
          setEditError("");     // ✅ Clear errors
          setEditFileError("");
          setEditPrivacy('public'); // ✅ Clear file errors
        }}
        maxWidth="sm"
        fullWidth
      >

        <DialogTitle>Edit Presentation</DialogTitle>
        <DialogContent>
       
          <TextField
            label="Title"
            value={editTitle}
            required
            size="small"
            fullWidth
            margin="dense"
            onClick={() => {
              setTempEditTitle(editTitle);
              setOpenEditTitleModal(true);
            }}
            InputProps={{
              readOnly: true,
              style: { cursor: 'pointer' }
            }}
            placeholder="Click to edit title..."
          />

          <TextField
            label="Type"
            select
            value={editType}
            onChange={(e) => setEditType(e.target.value)}
            size="small"
            fullWidth
            margin="dense"
          >
            <MenuItem value="company_specific_start">Company Specific - Start</MenuItem>
            <MenuItem value="company_specific_end">Company Specific - End</MenuItem>
            <MenuItem value="services">Services</MenuItem>
            <MenuItem value="solutions">Solutions</MenuItem>
            <MenuItem value="case_study">Case Study</MenuItem>
          </TextField>

          <TextField
            label="Tags (comma separated)"
            value={editTags}
            size="small"
            fullWidth
            margin="dense"
            onClick={() => {
              setTempEditTags(editTags);
              setOpenEditTagsModal(true);
            }}
            InputProps={{
              readOnly: true,
              style: { cursor: 'pointer' }
            }}
            placeholder="Click to edit tags..."
          />

          <TextField
            label="Privacy"
            select
            value={editPrivacy || 'public'}
            onChange={(e) => setEditPrivacy(e.target.value)}
            size="small"
            fullWidth
            margin="dense"
          >
            <MenuItem value="public">Public (visible to all users)</MenuItem>
            <MenuItem value="private">Private (only visible to me)</MenuItem>
          </TextField>

          {/* ✅ NEW: Status Dropdown */}
          <TextField
            label="Status"
            select
            value={editUploadStatus || 'final'}
            onChange={(e) => setEditUploadStatus(e.target.value)}
            size="small"
            fullWidth
            margin="dense"
          >
            <MenuItem value="final">Final (Ready to Use)</MenuItem>
            <MenuItem value="draft">Draft (Work in Progress)</MenuItem>
          </TextField>

            <TextField
            label="Internal Notes/FAQ"
            value={editNotes}
            size="small"
            fullWidth
            margin="dense"
            onClick={() => {
              setTempEditNotes(editNotes);
              setOpenEditNotesModal(true);
            }}
            InputProps={{
              readOnly: true,
              style: { cursor: 'pointer' }
            }}
            placeholder="Click to edit notes..."
          />

          {/* File Upload Section */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Change PPT File (Optional)
            </Typography>
            <input
              type="file"
              accept=".ppt,.pptx"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setEditFile(file);
                setEditFileError("");
              }}
              style={{ display: 'none' }}
              id="edit-file-upload"
            />
            <label htmlFor="edit-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                size="small"
                sx={{ mr: 1 }}
              >
                Choose New File
              </Button>
            </label>
            
            {editFile && (
              <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                Selected: {editFile.name} • {Math.round(editFile.size / 1024)} KB
              </Typography>
            )}
            
            {!editFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Leave empty to keep current file
              </Typography>
            )}
            
            {editFile && !isEditPpt && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                Invalid file type. Please choose .ppt/.pptx file.
              </Typography>
            )}
            
            {editFileError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {editFileError}
              </Typography>
            )}
          </Box>
          
          {editError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {editError}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button 
          onClick={() => {
            setOpenEdit(false);
            setEditSaving(false);  // ✅ Reset saving state
            setEditError("");     // ✅ Clear errors
            setEditFileError(""); // ✅ Clear file errors
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleEditSave}
          disabled={editSaving || !canEditSubmit}
          variant="contained"
          sx={{ minWidth: 80 }}
        >
          {editSaving ? 'Saving...' : 'Save'}
        </Button>
        </DialogActions>
       </Dialog>

      {/* ✅ NEW: Edit Title Modal Dialog */}
      <Dialog 
        open={openEditTitleModal} 
        onClose={() => setOpenEditTitleModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Presentation Title
          <IconButton
            aria-label="close"
            onClick={() => setOpenEditTitleModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={tempEditTitle}
            onChange={(e) => setTempEditTitle(e.target.value)}
            placeholder="Enter a descriptive title for your presentation"
            helperText={`${(tempEditTitle || '').length} characters`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditTitleModal(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setEditTitle(tempEditTitle);
              setOpenEditTitleModal(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ NEW: Edit Tags Modal Dialog */}
      <Dialog 
        open={openEditTagsModal} 
        onClose={() => setOpenEditTagsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Tags
          <IconButton
            aria-label="close"
            onClick={() => setOpenEditTagsModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tags (comma-separated)"
            type="text"
            fullWidth
            multiline
            rows={6}
            value={tempEditTags}
            onChange={(e) => setTempEditTags(e.target.value)}
            placeholder="e.g., marketing, sales, technology, annual report"
            helperText={`${(tempEditTags || '').split(',').filter(t => t.trim()).length} tags • ${(tempEditTags || '').length} characters`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditTagsModal(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setEditTags(tempEditTags);
              setOpenEditTagsModal(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* ✅ NEW: Edit Notes Modal Dialog */}
      <Dialog 
        open={openEditNotesModal} 
        onClose={() => setOpenEditNotesModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Internal Notes/FAQ
          <IconButton
            aria-label="close"
            onClick={() => setOpenEditNotesModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Notes/FAQ"
            type="text"
            fullWidth
            multiline
            rows={8}
            value={tempEditNotes}
            onChange={(e) => setTempEditNotes(e.target.value)}
            placeholder="Add internal notes, FAQs, or important information..."
            helperText={`${(tempEditNotes || '').length} characters`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditNotesModal(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setEditNotes(tempEditNotes);
              setOpenEditNotesModal(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ NEW: Notes Modal Dialog */}
      <Dialog 
        open={openNotesModal} 
        onClose={() => setOpenNotesModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Internal Notes/FAQ
          <IconButton
            aria-label="close"
            onClick={() => setOpenNotesModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Notes/FAQ"
            type="text"
            fullWidth
            multiline
            rows={8}
            value={tempNotes}
            onChange={(e) => setTempNotes(e.target.value)}
            placeholder="Add internal notes, FAQs, or important information about this presentation..."
            helperText={`${(tempNotes || '').length} characters`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNotesModal(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setNotes(tempNotes);
              setOpenNotesModal(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Delete Confirmation Dialog ===== */}

      <DeleteConfirmDialog
        open={confirmOpen}
        title={targetUpload?.title}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          if (targetUpload) {
            handleDelete(targetUpload);
            setConfirmOpen(false);
          }
        }}
      />
      {/* Delete Collection Confirmation Dialog */}
      <DeleteConfirmDialog
        open={openDeleteCollectionConfirm}
        title={collectionToDelete?.name}
        onClose={() => setOpenDeleteCollectionConfirm(false)}
        onConfirm={() => {
          if (collectionToDelete) {
            deleteCollection(collectionToDelete.id, collectionToDelete.name);
          }
          setOpenDeleteCollectionConfirm(false);
        }}
      />      
      {/* ✅ INSERT ALL THE SAVE COLLECTION DIALOG CODE HERE */}
      <Dialog
        open={openSaveCollection}
        onClose={() => {
          setOpenSaveCollection(false);
          setSaveCollectionError("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Slide Collection</DialogTitle>
        <DialogContent>
          <TextField
            label="Collection Name"
            value={saveCollectionName}
            onChange={(e) => setSaveCollectionName(e.target.value)}
            fullWidth
            margin="dense"
            required
            placeholder="e.g. Marketing Intro, Tech Overview"
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will save {selectedSlides.length} selected slides in their current order.
          </Typography>
          
          {saveCollectionError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {saveCollectionError}
            </Typography>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenSaveCollection(false);
              setSaveCollectionError("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={saveCollection}
            disabled={saveCollectionLoading || !saveCollectionName.trim() || selectedSlides.length === 0}
            variant="contained"
            sx={{ minWidth: 80 }}
          >
            {saveCollectionLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Collections Dialog (already exists) */}
      <Dialog
        open={openCollections}
        onClose={() => setOpenCollections(false)}
        maxWidth="md"
        fullWidth
      ></Dialog>


      {/* ✅ ADD THIS COLLECTIONS DIALOG */}
      <Dialog
        open={openCollections}
        onClose={() => setOpenCollections(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">Slide Collections</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={loadCollections}
              disabled={collectionsLoading}
            >
              {collectionsLoading ? 'Loading...' : 'Refresh'}
            </Button>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ minHeight: 400 }}>
          {collectionsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading collections...</Typography>
            </Box>
          )}
          
          {collectionsError && (
            <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
              {collectionsError}
            </Typography>
          )}
          
          {!collectionsLoading && !collectionsError && collections.length === 0 && (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No saved collections yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Save slide selections from preview to create collections
              </Typography>
            </Box>
          )}
          
          {!collectionsLoading && collections.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {collections.map((collection) => (
                <Card key={collection.id} sx={{ p: 2, border: '1px solid #eee' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
                      <Box sx={{ maxWidth: '400px' }}>
                        <TitleDisplay title={collection.name} maxLength={40} />
                      </Box>
                      {collection.description && (
                        <Box sx={{ 
                          maxWidth: '450px',
                          mt: 0.5,
                          '& > span': {
                            color: '#666 !important',
                            fontSize: '0.875rem !important'
                            }
                          }}>
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {collection.slideCount} slides  
                      </Typography>
                      </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => previewCollection(collection)}
                        disabled={collectionsLoading}
                      >
                        Preview
                      </Button>                   
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setCollectionToDelete(collection);
                          setOpenDeleteCollectionConfirm(true);
                        }}
                        disabled={collectionsLoading}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Box>

                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOpenCollections(false)}>Close</Button>
              <Button
              variant="outlined"
              onClick={() => {
                if (selectedSlides.length === slideOrder.length) {
                  // Deselect all
                  setSelectedSlides([]);
                } else {
                  // Select all
                  setSelectedSlides([...slideOrder]);
                }
              }}
              sx={{
                borderColor: '#4D99A3',
                color: '#4D99A3',
                '&:hover': {
                  backgroundColor: 'rgba(77, 153, 163, 0.1)',
                  borderColor: '#4D99A3'
                }
              }}
            >
              {selectedSlides.length === slideOrder.length ? 'Deselect All' : 'Select All'}
            </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ ADD PPT DIALOG - NEW */}
      <Dialog
        open={openAddPPT}
        onClose={() => setOpenAddPPT(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add PPT to Preview</DialogTitle>
        
        <DialogContent>
          {loadingAddPPT ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading available PPTs...</Typography>
            </Box>
          ) : (
            <>
              {availablePPTs.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No additional PPTs available
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    All PPTs are already in the preview
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* 🚀 NEW: Search Bar */}
                  <TextField
                    placeholder="Search PPTs by title..."
                    size="small"
                    fullWidth
                    value={searchAddPPT}
                    onChange={(e) => setSearchAddPPT(e.target.value)}
                    sx={{ mb: 2 }}
                    inputProps={{ autoComplete: "off" }}
                  />

                  {/* PPT Selection */}
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Select PPT to Add:
                  </Typography>
                  
                  {/* 🎯 FIXED: Scrollable container with proper height */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1.5,
                    maxHeight: '360px',  // ✅ Fixed 6 PPTs max (each ~60px height)
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    mb: 3,
                    pr: 1,
                    border: '1px solid #eee',
                    borderRadius: 1,
                    p: 1,
                    '&::-webkit-scrollbar': {
                      width: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f1f1',
                      borderRadius: '10px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#4D99A3',
                      borderRadius: '10px',
                      '&:hover': {
                        backgroundColor: '#3a7a83'
                      }
                    }
                  }}>
                    {availablePPTs
                      .filter(ppt =>
                        ppt.title.toLowerCase().includes(searchAddPPT.toLowerCase())
                      )
                      .map((ppt) => (
                        <Card
                          key={ppt.id}
                          sx={{
                            p: 1.5,  // ✅ Reduced padding
                            minHeight: '50px',  // ✅ Add fixed height
                            display: 'flex',  // ✅ Add flex
                            alignItems: 'center',  // ✅ Vertically center
                            border: selectedPPTToAdd === ppt.id ? '2px solid #4D99A3' : '1px solid #e0e0e0',
                            backgroundColor: selectedPPTToAdd === ppt.id ? 'rgba(77, 153, 163, 0.05)' : 'white',
                            '&:hover': {
                              backgroundColor: selectedPPTToAdd === ppt.id ? 'rgba(77, 153, 163, 0.05)' : '#f5f5f5',
                              borderColor: '#4D99A3',
                              cursor: 'pointer'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            width: '100%',  // ✅ Full width
                            gap: 1  // ✅ Add spacing
                          }}>
                            {/* Left: PPT Info - Clickable to select */}
                          <Box 
                            sx={{ 
                              flex: 1, 
                              minWidth: 0, 
                              cursor: 'pointer' 
                            }}
                            onClick={() => setSelectedPPTToAdd(ppt.id)}
                          >
                            <Box sx={{ maxWidth: '350px' }}>
                              <TitleDisplay title={ppt.title} maxLength={45} />
                            </Box>
                          </Box>                                     
                            {/* Right: Action Buttons */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                              {/* Eye Icon - Preview */}
                              <Tooltip title="Preview slides">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickPreview(ppt.id, ppt.title);
                                  }}
                                  sx={{
                                    color: '#666',
                                    '&:hover': {
                                      backgroundColor: 'rgba(77, 153, 163, 0.1)',
                                      color: '#4D99A3'
                                    }
                                  }}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              {/* Checkmark when selected */}
                              {selectedPPTToAdd === ppt.id && (
                                <CheckCircle sx={{ color: '#4D99A3' }} />
                              )}
                            </Box>
                          </Box>
                        </Card>
                      ))}
                    
                    {/* No results message */}
                    {availablePPTs.filter(ppt =>
                      ppt.title.toLowerCase().includes(searchAddPPT.toLowerCase())
                    ).length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No PPTs match "{searchAddPPT}"
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Insert Position Selection */}
                  {selectedPPTToAdd && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                        Insert Position:
                      </Typography>
                      
                      <Select
                        value={insertPosition}
                        onChange={(e) => setInsertPosition(e.target.value)}
                        fullWidth
                        sx={{ mb: 2 }}
                      >
                        <MenuItem value="end">At the end (after all slides)</MenuItem>
                        <ListSubheader>After specific slide:</ListSubheader>
                        {slideOrder.map((slideIdx, index) => (
                          <MenuItem key={slideIdx} value={index.toString()}>
                            After slide {index + 1}
                          </MenuItem>
                        ))}
                      </Select>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {availablePPTs.find(p => p.id === selectedPPTToAdd)?.slidecount || 0} slides will be inserted 
                        {insertPosition === 'end' 
                          ? ' at the end of the preview' 
                          : ` after slide ${parseInt(insertPosition) + 1}`}
                      </Typography>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenAddPPT(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddPPTToPreview}
            disabled={!selectedPPTToAdd || loadingAddPPT || availablePPTs.length === 0}
            sx={{ 
              backgroundColor: '#4D99A3',
              '&:hover': { backgroundColor: '#3d8a94' }
            }}
          >
            {loadingAddPPT ? 'Adding...' : 'Add to Preview'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Preview Dialog - Simple Slides View Only */}
      <Dialog
        open={quickPreview.open}
        onClose={() => setQuickPreview({ open: false, pptId: null, pptTitle: '', slides: [] })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <TitleDisplay title={quickPreview.pptTitle} maxLength={40} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {quickPreview.slides.length} {quickPreview.slides.length === 1 ? 'slide' : 'slides'}
            </Typography>
          </Box>
        </DialogTitle>      
        <DialogContent sx={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
          {quickPreview.slides.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 1,  // ✅ Reduced from 2 to 1
          p: 1.5   // ✅ Reduced from 2 to 1.5
        }}>
      {quickPreview.slides.map((slide, index) => (
        <Box
          key={index}
          sx={{
            position: 'relative',
            aspectRatio: '16/9',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: '#f5f5f5'
          }}
        >
          <img
            src={slide}
            alt={`Slide ${index + 1}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 4,
              right: 4,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              fontSize: '0.7rem'
            }}
          >
            {index + 1}
          </Typography>
        </Box>
      ))}
    </Box>
  )}
</DialogContent>


        <DialogActions>
          <Button 
            onClick={() => setQuickPreview({ open: false, pptId: null, pptTitle: '', slides: [] })}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}

      <Snackbar
        open={showSuccessNotification}
        autoHideDuration={3000}
        onClose={() => setShowSuccessNotification(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessNotification(false)}
          severity="success"
          variant="filled"
          icon={<CheckCircle />}
          sx={{
            width: '100%',
            backgroundColor: '#4D99A3',
            color: 'white',
            fontSize: '16px',
            fontWeight: 'bold',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

    </Box>
          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
          {/* Single Slide Preview Dialog - Compact & Professional Design */}
          <Dialog
            open={openSingleSlideDialog}
            onClose={() => setOpenSingleSlideDialog(false)}
            maxWidth="md"  // ✅ Changed from "xl" to "md" for smaller width
            fullWidth
            PaperProps={{
              sx: {
                backgroundColor: '#f8f9fa',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                minHeight: '70vh',
                maxHeight: '85vh',
                maxWidth: '900px'  // ✅ Maximum width limit
              }
            }}
          >
            <DialogTitle sx={{ 
              background: 'linear-gradient(135deg, #c3e2e6ff 0%, #f8f9fa 100%)',
              color: '#141313ff',  // ✅ Changed to dark color for better visibility
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '14px 20px',  // ✅ Reduced padding
              borderRadius: '16px 16px 0 0'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Visibility sx={{ fontSize: '24px', color: '#4D99A3' }} />  {/* ✅ Smaller icon */}
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#141313ff' }}>
                  Slide Preview - Slide {singleSlideNumber}
                </Typography>
              </Box>
              <IconButton 
                onClick={() => setOpenSingleSlideDialog(false)}
                sx={{ 
                  color: '#000000ff',
                  backgroundColor: '#d9edf0ff',
                  padding: '6px',  // ✅ Smaller button
                  '&:hover': {
                    backgroundColor: 'rgba(209, 140, 140, 0.2)',
                    transform: 'rotate(90deg)',
                    transition: 'all 0.4s ease'
                  }
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ 
              backgroundColor: '#f8f9fa',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 3,  // ✅ Reduced padding from 4 to 3
              position: 'relative'
            }}>
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#ffffff',  // ✅ Pure white for slide background
                  borderRadius: '12px',
                  padding: '16px',  // ✅ Reduced padding
                  boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
              >
                <Box
                  component="img"
                  src={singleSlideUrl}
                  alt={`Slide ${singleSlideNumber}`}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '60vh',  // ✅ Reduced from 65vh to 60vh
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(77, 153, 163, 0.2)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    }
                  }}
                />
              </Box>
            </DialogContent>
            
            <Box sx={{ 
              backgroundColor: '#f8f9fa',
              padding: '10px 20px',  // ✅ Reduced padding
              borderRadius: '0 0 16px 16px',
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                Click outside or press [ X ] to close
              </Typography>
            </Box>
          </Dialog>
          {/* Step 1: Ask whether they have an RFP */}
<Dialog open={openRfpChoice} onClose={() => setOpenRfpChoice(false)} maxWidth="sm" fullWidth>
  <DialogTitle>Customize before download?</DialogTitle>
  <DialogContent>
    <Typography>
      Do you have an RFP or requirement document for this presentation? If you upload one,
      we'll automatically pull the customer name, project name, and project brief and fill
      them into the PPT.
    </Typography>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => proceedToDownload(null)}>
      No, download as is
    </Button>
    <Button
      variant="contained"
      onClick={() => { setOpenRfpChoice(false); setOpenRfpUpload(true); }}
      sx={{ backgroundColor: '#4D99A3' }}
    >
      Yes, upload RFP
    </Button>
  </DialogActions>
</Dialog>

{/* Step 2: Upload + extract */}
<Dialog open={openRfpUpload} onClose={() => !rfpExtracting && setOpenRfpUpload(false)} maxWidth="sm" fullWidth>
  <DialogTitle>Upload RFP / Requirement Document</DialogTitle>
  <DialogContent>
    <Stack spacing={2} sx={{ mt: 1 }}>
      {!rfpExtracted ? (
        <>
          <input
            id="rfp-input"
            type="file"
            accept=".pdf,.doc,.docx"
            style={{ display: "none" }}
            onChange={(e) => { setRfpFile(e.target.files?.[0] || null); setRfpError(""); }}
          />
          <label htmlFor="rfp-input">
            <Button variant="outlined" component="span" startIcon={<CloudUpload />}>
              Choose file
            </Button>
          </label>
          <Typography variant="body2" color="text.secondary">
            {rfpFile ? `${rfpFile.name} • ${Math.round(rfpFile.size / 1024)} KB` : "PDF or Word, up to 20MB."}
          </Typography>

          {rfpExtracting && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Reading document and extracting details…</Typography>
            </Box>
          )}

          {rfpError && <Alert severity="error">{rfpError}</Alert>}
        </>
      ) : (
        <>
          <Alert severity="success" sx={{ mb: 1 }}>
            Fields extracted successfully. Review and confirm below.
          </Alert>
          <TextField
            label="Project Name"
            size="small"
            fullWidth
            value={rfpExtracted.projectName || ""}
            onChange={(e) => setRfpExtracted(prev => ({ ...prev, projectName: e.target.value }))}
          />
          <TextField
            label="Customer Name"
            size="small"
            fullWidth
            value={rfpExtracted.customerName || ""}
            onChange={(e) => setRfpExtracted(prev => ({ ...prev, customerName: e.target.value }))}
          />
          <TextField
            label="Company Name"
            size="small"
            fullWidth
            value={rfpExtracted.companyName || ""}
            onChange={(e) => setRfpExtracted(prev => ({ ...prev, companyName: e.target.value }))}
          />
          <TextField
            label="Company Brief"
            size="small"
            fullWidth
            multiline
            rows={3}
            value={rfpExtracted.companyBrief || ""}
            onChange={(e) => setRfpExtracted(prev => ({ ...prev, companyBrief: e.target.value }))}
          />
          <TextField
            label="Solution Topic (replaces 'Solution' on slide 1)"
            size="small"
            fullWidth
            value={rfpExtracted.solutionTopic || ""}
            onChange={(e) => setRfpExtracted(prev => ({ ...prev, solutionTopic: e.target.value }))}
          />
          <TextField
            label="Business Objective (Executive Summary – slide 4)"
            size="small"
            fullWidth
            multiline
            rows={3}
            value={rfpExtracted.businessObjective || ""}
            onChange={(e) => setRfpExtracted(prev => ({ ...prev, businessObjective: e.target.value }))}
          />
          <TextField
            label="Goals (Executive Summary – slide 4)"
            size="small"
            fullWidth
            multiline
            rows={3}
            value={rfpExtracted.goals || ""}
            onChange={(e) => setRfpExtracted(prev => ({ ...prev, goals: e.target.value }))}
          />
          <TextField
            label="Approach (Executive Summary – slide 4)"
            size="small"
            fullWidth
            multiline
            rows={3}
            value={rfpExtracted.approach || ""}
            onChange={(e) => setRfpExtracted(prev => ({ ...prev, approach: e.target.value }))}
          />
          <Typography variant="caption" color="text.secondary">
            These values replace placeholder text in the downloaded PPT: "Company Name", "Customer Name", "Project Name", "Company Brief", "Solution" (slide 1 heading area), and the content boxes labelled "Objective Content", "Goals Content", "Approach Content" on slide 4. You can edit any field before confirming.
          </Typography>
        </>
      )}
    </Stack>
  </DialogContent>
  <DialogActions>
    <Button
      onClick={() => { setOpenRfpUpload(false); setRfpExtracted(null); setRfpFile(null); proceedToDownload(null); }}
      disabled={rfpExtracting}
    >
      Skip
    </Button>
    {!rfpExtracted ? (
      <Button
        variant="contained"
        onClick={handleRfpSubmit}
        disabled={!rfpFile || rfpExtracting}
        sx={{ backgroundColor: '#4D99A3' }}
      >
        {rfpExtracting ? 'Extracting…' : 'Extract & Continue'}
      </Button>
    ) : (
      <Button
        variant="contained"
        onClick={() => {
          const fields = rfpExtracted;
          setRfpExtracted(null);
          setRfpFile(null);
          proceedToDownload(fields);
        }}
        sx={{ backgroundColor: '#4D99A3' }}
      >
        Confirm & Download
      </Button>
    )}
  </DialogActions>
</Dialog>
      </Box>
    );
}